import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, DollarSign, TrendingUp, CheckCircle, XCircle, Clock, 
  Car, Search, ArrowLeft, Settings, MapPin, Truck, Package,
  Loader2, RefreshCw, AlertCircle, Activity, Plus, Trash2, Edit2, Image, Percent,
  Banknote, Send, Calendar, ToggleLeft, ToggleRight, Upload, BarChart3, Gift, Tag, Edit, FileText, Map, UserPlus, User, Key, X, Globe, Save
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import AdminDriverEdit from '../components/AdminDriverEdit';
import LiveDriversMap from '../components/LiveDriversMap';

const AdminPortal = () => {
  const navigate = useNavigate();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingDriverId, setEditingDriverId] = useState(null);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [rides, setRides] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  
  // Filter states
  const [driverFilter, setDriverFilter] = useState('all');
  const [driverSearch, setDriverSearch] = useState('');
  const [rideFilter, setRideFilter] = useState('all');
  
  // Edit states
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [editingDriverPayout, setEditingDriverPayout] = useState(null);
  const [newPayoutPercentage, setNewPayoutPercentage] = useState('');
  
  // Payouts states
  const [pendingPayouts, setPendingPayouts] = useState({ drivers: [], total_pending: 0, driver_count: 0 });
  const [payoutSettings, setPayoutSettings] = useState({ auto_payout_enabled: false, auto_payout_day: 0, minimum_payout_amount: 10, paypal_configured: false });
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [isProcessingPayout, setIsProcessingPayout] = useState(false);
  const [selectedDriversForPayout, setSelectedDriversForPayout] = useState([]);
  
  // Users states
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: '', email: '', phone: '', password: 'Welcome123!' });
  
  // 1099 Tax states
  const [tax1099Data, setTax1099Data] = useState(null);
  const [tax1099Year, setTax1099Year] = useState(new Date().getFullYear());
  const [isLoadingTax, setIsLoadingTax] = useState(false);
  const [isGenerating1099, setIsGenerating1099] = useState(false);
  const [isSending1099, setIsSending1099] = useState(false);
  
  // Tips states
  const [tips, setTips] = useState([]);
  const [tipSettings, setTipSettings] = useState({
    enabled: true,
    preset_amounts: [2, 5, 10, 15],
    custom_allowed: true,
    default_percentage: 15
  });
  const [isLoadingTips, setIsLoadingTips] = useState(false);
  
  // Promo codes states
  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_type: 'percentage', // 'percentage' or 'fixed'
    discount_value: 10,
    max_uses: 100,
    valid_until: '',
    min_ride_amount: 0,
    is_active: true,
    description: ''
  });
  const [isLoadingPromos, setIsLoadingPromos] = useState(false);
  
  // Dispatch & Active Rides states
  const [pendingRides, setPendingRides] = useState({ rides: [], total: 0, urgent_count: 0 });
  const [activeRidesData, setActiveRidesData] = useState({ rides: [], total: 0, counts: {} });
  const [unpaidRides, setUnpaidRides] = useState({ rides: [], total: 0 });
  const [isLoadingDispatch, setIsLoadingDispatch] = useState(false);
  const [selectedRideForDispatch, setSelectedRideForDispatch] = useState(null);
  const [nearbyDriversForDispatch, setNearbyDriversForDispatch] = useState([]);
  const [isAssigningDriverDispatch, setIsAssigningDriverDispatch] = useState(false);
  const [dispatchTab, setDispatchTab] = useState('pending'); // 'pending', 'active', or 'unpaid'
  
  // Referral Program states
  const [referralStats, setReferralStats] = useState(null);
  const [referralSettings, setReferralSettings] = useState({
    referrer_reward: 5.00,
    referee_reward: 5.00,
    min_rides_for_reward: 1,
    enabled: true
  });
  const [referralList, setReferralList] = useState([]);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  const [isSavingReferralSettings, setIsSavingReferralSettings] = useState(false);
  
  // Driver Referral Program states
  const [driverReferralStats, setDriverReferralStats] = useState(null);
  const [driverReferralSettings, setDriverReferralSettings] = useState({
    referrer_reward: 25.00,
    referee_reward: 25.00,
    min_rides_for_reward: 10,
    enabled: true
  });
  const [isSavingDriverReferralSettings, setIsSavingDriverReferralSettings] = useState(false);
  const [driverReferralPayouts, setDriverReferralPayouts] = useState({ referrals: [], summary: {} });
  const [isLoadingDriverPayouts, setIsLoadingDriverPayouts] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(null);
  
  // Unassigned scheduled rides state
  const [unassignedRides, setUnassignedRides] = useState({ rides: [], total: 0, urgent_count: 0 });
  const [isLoadingUnassigned, setIsLoadingUnassigned] = useState(false);
  const [nearbyDriversForRide, setNearbyDriversForRide] = useState(null);
  const [selectedRideForAssignment, setSelectedRideForAssignment] = useState(null);
  const [isAssigningDriver, setIsAssigningDriver] = useState(false);
  
  // Password Reset Modal states
  const [passwordResetModal, setPasswordResetModal] = useState({ show: false, email: '', userType: '', name: '' });
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [sendPasswordNotification, setSendPasswordNotification] = useState(true);
  
  // Terms & Conditions states
  const [termsContent, setTermsContent] = useState('');
  const [termsTitle, setTermsTitle] = useState('Terms & Conditions');
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  
  // New vehicle form
  const [newVehicle, setNewVehicle] = useState({
    name: '',
    description: '',
    image_url: '',
    service_types: ['transportation'],
    base_fare: 0,
    per_mile: 0,
    per_minute: 0,
    minimum_fare: 0,
    booking_fee: 2,
    per_stop_fee: 0,
    hourly_rate: 0,
    max_passengers: 4,
    is_active: true
  });

  // CMS Content State
  const [cmsContent, setCmsContent] = useState(null);
  const [cmsLoading, setCmsLoading] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({ title: '', links: [] });

  // Check admin login
  useEffect(() => {
    const adminId = localStorage.getItem('admin_id');
    if (!adminId) {
      toast({
        title: "Please Login",
        description: "Admin authentication required",
        variant: "destructive"
      });
      navigate('/admin/login');
      return;
    }
    
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchDrivers(),
        fetchRides(),
        fetchVehicles(),
        fetchPendingPayouts(),
        fetchPayoutSettings(),
        fetchPayoutHistory(),
        fetchTipSettings(),
        fetchPromoCodes(),
        fetchTermsConditions(),
        fetchUsers(),
        fetchDispatchData()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  // Fetch all users
  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const params = new URLSearchParams();
      if (userSearch) params.append('search', userSearch);
      const response = await axios.get(`${BACKEND_URL}/api/admin/users?${params.toString()}`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({ title: "Error", description: "Failed to fetch users", variant: "destructive" });
    } finally {
      setIsLoadingUsers(false);
    }
  };
  
  // Get user details
  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}`);
      setSelectedUser(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch user details", variant: "destructive" });
    }
  };
  
  // Delete user
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`⚠️ DELETE USER ${userName}?\n\nThis will remove the user from the system. This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`);
      toast({ title: "User Deleted", description: `${userName} has been removed from the system` });
      fetchUsers();
      fetchStats();
      setSelectedUser(null);
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete user", variant: "destructive" });
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.full_name || !newUser.email || !newUser.phone) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users`, newUser);
      toast({ title: "User Created", description: `${newUser.full_name} has been added to the system` });
      setIsAddingUser(false);
      setNewUser({ full_name: '', email: '', phone: '', password: 'Welcome123!' });
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to create user", variant: "destructive" });
    }
  };

  // Admin Password Reset
  const openPasswordResetModal = (email, userType, name) => {
    setPasswordResetModal({ show: true, email, userType, name });
    setNewPasswordInput('');
    setSendPasswordNotification(true);
  };

  const handleAdminPasswordReset = async () => {
    if (!newPasswordInput || newPasswordInput.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    
    try {
      setIsResettingPassword(true);
      const response = await axios.post(`${BACKEND_URL}/api/admin/reset-password`, {
        email: passwordResetModal.email,
        user_type: passwordResetModal.userType,
        new_password: newPasswordInput,
        send_notification: sendPasswordNotification
      });
      
      const notificationMsg = response.data.notification_sent 
        ? ' SMS notification sent to user.' 
        : '';
      
      toast({ 
        title: "Password Reset Successful", 
        description: `Password for ${passwordResetModal.name} has been reset.${notificationMsg}` 
      });
      
      setPasswordResetModal({ show: false, email: '', userType: '', name: '' });
      setNewPasswordInput('');
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to reset password", 
        variant: "destructive" 
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Fetch 1099 tax data
  const fetch1099Data = async () => {
    try {
      setIsLoadingTax(true);
      const response = await axios.get(`${BACKEND_URL}/api/tax/1099/${tax1099Year}`);
      setTax1099Data(response.data);
    } catch (error) {
      console.error('Error fetching 1099 data:', error);
      toast({ title: "Error", description: "Failed to fetch 1099 data", variant: "destructive" });
    } finally {
      setIsLoadingTax(false);
    }
  };

  // Generate 1099 forms
  const handleGenerate1099 = async () => {
    if (!window.confirm(`Generate 1099-NEC forms for all drivers with earnings ≥ $600 in ${tax1099Year}?\n\nEmail notifications will be sent to all eligible drivers.`)) {
      return;
    }
    
    try {
      setIsGenerating1099(true);
      const response = await axios.post(`${BACKEND_URL}/api/tax/1099/generate`, {
        year: tax1099Year
      });
      const emailsSent = response.data.emails_sent || 0;
      toast({ 
        title: "1099 Forms Generated", 
        description: `Generated ${response.data.forms_generated} forms for year ${tax1099Year}. ${emailsSent} email notification${emailsSent !== 1 ? 's' : ''} sent.` 
      });
      fetch1099Data();
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate 1099 forms", variant: "destructive" });
    } finally {
      setIsGenerating1099(false);
    }
  };

  // Send 1099 to all drivers
  const handleSend1099ToAll = async () => {
    if (!window.confirm(`Mark all 1099 forms as sent to drivers for ${tax1099Year}? This indicates drivers have been notified.`)) {
      return;
    }
    
    try {
      setIsSending1099(true);
      await axios.post(`${BACKEND_URL}/api/tax/1099/send/${tax1099Year}`);
      toast({ title: "1099 Forms Sent", description: `All drivers have been notified for year ${tax1099Year}` });
      fetch1099Data();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send 1099 notifications", variant: "destructive" });
    } finally {
      setIsSending1099(false);
    }
  };
  
  // Fetch tip settings
  const fetchTipSettings = async () => {
    try {
      setIsLoadingTips(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/tips/settings`);
      setTipSettings(response.data);
    } catch (error) {
      console.error('Error fetching tip settings:', error);
    } finally {
      setIsLoadingTips(false);
    }
  };
  
  // Update tip settings
  const updateTipSettings = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tips/settings`, tipSettings);
      toast({ title: "Success", description: "Tip settings updated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update tip settings", variant: "destructive" });
    }
  };
  
  // Fetch promo codes
  const fetchPromoCodes = async () => {
    try {
      setIsLoadingPromos(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/promos`);
      setPromoCodes(response.data.promos || []);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setIsLoadingPromos(false);
    }
  };
  
  // Create promo code
  const createPromoCode = async () => {
    if (!newPromo.code) {
      toast({ title: "Error", description: "Promo code is required", variant: "destructive" });
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/promos`, newPromo);
      toast({ title: "Success", description: "Promo code created" });
      setNewPromo({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        max_uses: 100,
        valid_until: '',
        min_ride_amount: 0,
        is_active: true,
        description: ''
      });
      fetchPromoCodes();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to create promo code", variant: "destructive" });
    }
  };
  
  // Toggle promo code active status
  const togglePromoStatus = async (promoId, currentStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/promos/${promoId}`, { is_active: !currentStatus });
      fetchPromoCodes();
      toast({ title: "Success", description: `Promo code ${!currentStatus ? 'activated' : 'deactivated'}` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update promo code", variant: "destructive" });
    }
  };
  
  // Delete promo code
  const deletePromoCode = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/promos/${promoId}`);
      fetchPromoCodes();
      toast({ title: "Success", description: "Promo code deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete promo code", variant: "destructive" });
    }
  };

  // Fetch Terms & Conditions
  const fetchTermsConditions = async () => {
    try {
      setIsLoadingTerms(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/terms`);
      setTermsContent(response.data.content || '');
      setTermsTitle(response.data.title || 'Terms & Conditions');
    } catch (error) {
      console.error('Error fetching terms:', error);
    } finally {
      setIsLoadingTerms(false);
    }
  };

  // Save Terms & Conditions
  const saveTermsConditions = async () => {
    try {
      setIsSavingTerms(true);
      await axios.put(`${BACKEND_URL}/api/admin/terms`, {
        title: termsTitle,
        content: termsContent
      });
      toast({ title: "Success", description: "Terms & Conditions updated successfully" });
    } catch (error) {
      console.error('Error saving terms:', error);
      toast({ title: "Error", description: "Failed to save Terms & Conditions", variant: "destructive" });
    } finally {
      setIsSavingTerms(false);
    }
  };

  // Fetch Dispatch Data (Pending and Active Rides)
  const fetchDispatchData = useCallback(async () => {
    try {
      setIsLoadingDispatch(true);
      const [pendingRes, activeRes, unpaidRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/rides/pending`).catch(() => ({ data: { rides: [], total: 0, urgent_count: 0 } })),
        axios.get(`${BACKEND_URL}/api/admin/rides/active`).catch(() => ({ data: { rides: [], total: 0, counts: {} } })),
        axios.get(`${BACKEND_URL}/api/admin/rides/unpaid`).catch(() => ({ data: { rides: [], total: 0 } }))
      ]);
      console.log('Dispatch data loaded:', pendingRes.data?.total || 0, 'pending rides,', unpaidRes.data?.total || 0, 'unpaid rides');
      setPendingRides(pendingRes.data || { rides: [], total: 0, urgent_count: 0 });
      setActiveRidesData(activeRes.data || { rides: [], total: 0, counts: {} });
      setUnpaidRides(unpaidRes.data || { rides: [], total: 0 });
    } catch (error) {
      console.error('Error fetching dispatch data:', error);
      // Set empty state on error - don't show error toast
      setPendingRides({ rides: [], total: 0, urgent_count: 0 });
      setActiveRidesData({ rides: [], total: 0, counts: {} });
      setUnpaidRides({ rides: [], total: 0 });
    } finally {
      setIsLoadingDispatch(false);
    }
  }, [BACKEND_URL]);

  // Fetch CMS Content
  const fetchCMSContent = async () => {
    setCmsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/content`);
      setCmsContent(response.data);
    } catch (error) {
      console.error('Error fetching CMS content:', error);
      toast({ title: "Error", description: "Failed to load website content", variant: "destructive" });
    } finally {
      setCmsLoading(false);
    }
  };

  // Save CMS Section
  const saveCMSSection = async (sectionKey, sectionData) => {
    try {
      const updatePayload = { [sectionKey]: sectionData };
      const response = await axios.put(`${BACKEND_URL}/api/cms/content`, updatePayload);
      setCmsContent(response.data.content);
      setEditingSection(null);
      toast({ title: "Success", description: "Website content updated" });
    } catch (error) {
      console.error('Error saving CMS content:', error);
      toast({ title: "Error", description: "Failed to save content", variant: "destructive" });
    }
  };

  // Save CMS Page Content (about, privacy, terms)
  const saveCMSPageContent = async (pageKey, content) => {
    try {
      const updatePayload = { [pageKey]: content };
      const response = await axios.put(`${BACKEND_URL}/api/cms/content`, updatePayload);
      setCmsContent(response.data.content);
      toast({ title: "Success", description: "Page content updated" });
    } catch (error) {
      console.error('Error saving page content:', error);
      toast({ title: "Error", description: "Failed to save page content", variant: "destructive" });
    }
  };

  // Add link to section
  const addLinkToSection = () => {
    setSectionForm(prev => ({
      ...prev,
      links: [...prev.links, { label: '', url: '', is_internal: true }]
    }));
  };

  // Remove link from section
  const removeLinkFromSection = (index) => {
    setSectionForm(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  // Update link in section
  const updateLinkInSection = (index, field, value) => {
    setSectionForm(prev => ({
      ...prev,
      links: prev.links.map((link, i) => i === index ? { ...link, [field]: value } : link)
    }));
  };

  // Fetch nearby drivers for a specific ride
  const fetchNearbyDriversForDispatch = async (rideId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/rides/${rideId}/nearby-drivers`);
      setNearbyDriversForDispatch(response.data.drivers || []);
      setSelectedRideForDispatch(response.data.ride);
    } catch (error) {
      console.error('Error fetching nearby drivers:', error);
      toast({ title: "Error", description: "Failed to load nearby drivers", variant: "destructive" });
    }
  };

  // Assign driver to ride from dispatch
  const assignDriverFromDispatch = async (rideId, driverId, driverName) => {
    try {
      setIsAssigningDriverDispatch(true);
      await axios.post(`${BACKEND_URL}/api/admin/rides/${rideId}/assign-driver/${driverId}`);
      toast({ 
        title: "Driver Assigned", 
        description: `${driverName} has been assigned to the ride. SMS notification sent.` 
      });
      setSelectedRideForDispatch(null);
      setNearbyDriversForDispatch([]);
      fetchDispatchData();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to assign driver", 
        variant: "destructive" 
      });
    } finally {
      setIsAssigningDriverDispatch(false);
    }
  };

  // Mark ride as paid manually (for webhook failures)
  const markRideAsPaid = async (rideId, passengerName) => {
    if (!window.confirm(`Mark ${passengerName}'s ride as PAID? This will move it to pending dispatch.`)) {
      return;
    }
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/rides/${rideId}/mark-paid`);
      toast({ 
        title: "Ride Marked as Paid", 
        description: `${passengerName}'s ride is now ready for dispatch` 
      });
      fetchDispatchData(); // Refresh all tabs
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to mark ride as paid", 
        variant: "destructive" 
      });
    }
  };

  // Delete a ride (unpaid or pending only)
  const deleteRide = async (rideId, passengerName) => {
    if (!window.confirm(`DELETE ${passengerName}'s ride? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/rides/${rideId}`);
      toast({ 
        title: "Ride Deleted", 
        description: `${passengerName}'s ride has been removed` 
      });
      fetchDispatchData(); // Refresh all tabs
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to delete ride", 
        variant: "destructive" 
      });
    }
  };

  // Notify nearby drivers about a pending ride
  const notifyNearbyDrivers = async (rideId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/rides/${rideId}/notify-drivers`);
      toast({ 
        title: "Notifications Sent", 
        description: `${response.data.notified_count} drivers have been notified via SMS` 
      });
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to notify drivers", 
        variant: "destructive" 
      });
    }
  };

  // Fetch Referral Stats and Settings
  const fetchReferralData = async () => {
    try {
      setIsLoadingReferrals(true);
      const response = await axios.get(`${BACKEND_URL}/api/referral/admin/stats`);
      setReferralStats(response.data.stats);
      setReferralSettings(response.data.settings);
      
      // Also fetch referral list
      const listResponse = await axios.get(`${BACKEND_URL}/api/referral/admin/list?limit=20`);
      setReferralList(listResponse.data.referrals || []);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setIsLoadingReferrals(false);
    }
  };

  // Save Referral Settings
  const saveReferralSettings = async () => {
    try {
      setIsSavingReferralSettings(true);
      await axios.put(`${BACKEND_URL}/api/referral/admin/settings`, referralSettings);
      toast({ title: "Success", description: "User referral settings updated successfully" });
    } catch (error) {
      console.error('Error saving referral settings:', error);
      toast({ title: "Error", description: "Failed to save referral settings", variant: "destructive" });
    } finally {
      setIsSavingReferralSettings(false);
    }
  };

  // Fetch driver referral stats and settings
  const fetchDriverReferralData = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/driver/admin/stats`);
      setDriverReferralStats(response.data);
      if (response.data.settings) {
        setDriverReferralSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching driver referral data:', error);
    }
  }, [BACKEND_URL]);

  // Save driver referral settings
  const saveDriverReferralSettings = async () => {
    try {
      setIsSavingDriverReferralSettings(true);
      await axios.put(`${BACKEND_URL}/api/referral/driver/admin/settings`, driverReferralSettings);
      toast({ title: "Success", description: "Driver referral settings updated successfully" });
      fetchDriverReferralData(); // Refresh stats
    } catch (error) {
      console.error('Error saving driver referral settings:', error);
      toast({ title: "Error", description: "Failed to save driver referral settings", variant: "destructive" });
    } finally {
      setIsSavingDriverReferralSettings(false);
    }
  };

  // Fetch driver referral payouts
  const fetchDriverReferralPayouts = useCallback(async () => {
    try {
      setIsLoadingDriverPayouts(true);
      const response = await axios.get(`${BACKEND_URL}/api/referral/driver/admin/payouts`);
      setDriverReferralPayouts(response.data);
    } catch (error) {
      console.error('Error fetching driver referral payouts:', error);
    } finally {
      setIsLoadingDriverPayouts(false);
    }
  }, [BACKEND_URL]);

  // Mark driver referral payout as paid
  const markReferralPaid = async (referralId, payoutType) => {
    try {
      setIsMarkingPaid(referralId);
      await axios.post(`${BACKEND_URL}/api/referral/driver/admin/payouts/${referralId}/mark-paid?payout_type=${payoutType}`);
      toast({ title: "Success", description: `Payout marked as paid` });
      fetchDriverReferralPayouts(); // Refresh list
      fetchDriverReferralData(); // Refresh stats
    } catch (error) {
      console.error('Error marking payout as paid:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to mark payout as paid';
      toast({ title: "Error", description: errorMsg, variant: "destructive" });
    } finally {
      setIsMarkingPaid(null);
    }
  };

  // Fetch unassigned scheduled rides
  const fetchUnassignedRides = useCallback(async () => {
    try {
      setIsLoadingUnassigned(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/ride-assignment/unassigned-scheduled`);
      setUnassignedRides(response.data);
    } catch (error) {
      console.error('Error fetching unassigned rides:', error);
    } finally {
      setIsLoadingUnassigned(false);
    }
  }, [BACKEND_URL]);

  // Fetch nearby drivers for a specific ride
  const fetchNearbyDriversForRide = async (rideId) => {
    try {
      setSelectedRideForAssignment(rideId);
      const response = await axios.get(`${BACKEND_URL}/api/admin/ride-assignment/nearby-drivers/${rideId}?max_eta_minutes=10`);
      setNearbyDriversForRide(response.data);
    } catch (error) {
      console.error('Error fetching nearby drivers:', error);
      toast({ title: "Error", description: "Failed to fetch nearby drivers", variant: "destructive" });
    }
  };

  // Assign driver to ride
  const assignDriverToRide = async (rideId, driverId) => {
    try {
      setIsAssigningDriver(true);
      await axios.post(`${BACKEND_URL}/api/admin/ride-assignment/assign-driver`, {
        ride_id: rideId,
        driver_id: driverId
      });
      toast({ title: "Success", description: "Driver assigned successfully!" });
      setNearbyDriversForRide(null);
      setSelectedRideForAssignment(null);
      fetchUnassignedRides();
      fetchRides();
    } catch (error) {
      console.error('Error assigning driver:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to assign driver';
      toast({ title: "Error", description: errorMsg, variant: "destructive" });
    } finally {
      setIsAssigningDriver(false);
    }
  };

  const fetchDrivers = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const params = new URLSearchParams();
      if (driverFilter !== 'all') params.append('status', driverFilter);
      if (driverSearch) params.append('search', driverSearch);
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/drivers?${params}`);
      setDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [BACKEND_URL, driverFilter, driverSearch]);

  const fetchRides = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (rideFilter !== 'all') params.append('status', rideFilter);
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/rides?${params}`);
      setRides(response.data.rides || []);
    } catch (error) {
      console.error('Error fetching rides:', error);
    }
  }, [BACKEND_URL, rideFilter]);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicles`);
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchPendingPayouts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payouts/pending`);
      setPendingPayouts(response.data);
    } catch (error) {
      console.error('Error fetching pending payouts:', error);
    }
  };

  const fetchPayoutSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payouts/settings`);
      setPayoutSettings(response.data);
    } catch (error) {
      console.error('Error fetching payout settings:', error);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payouts/history`);
      setPayoutHistory(response.data.batches || []);
    } catch (error) {
      console.error('Error fetching payout history:', error);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      fetchDrivers();
    }
  }, [driverFilter, driverSearch, fetchDrivers, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      fetchRides();
      fetchUnassignedRides(); // Also fetch unassigned scheduled rides
    }
  }, [rideFilter, fetchRides, fetchUnassignedRides, isLoading]);

  // Auto-refresh dispatch data every 30 seconds to catch new rides
  useEffect(() => {
    // Also fetch immediately on mount
    fetchDispatchData();
    
    const interval = setInterval(() => {
      fetchDispatchData();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [fetchDispatchData]);

  const handleApproveDriver = async (driverId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/drivers/${driverId}/approve`);
      toast({ title: "Driver Approved", description: "Driver can now start accepting rides" });
      fetchDrivers();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to approve driver", variant: "destructive" });
    }
  };

  const handleRejectDriver = async (driverId, driverName) => {
    const reason = window.prompt(`Enter reason for rejecting ${driverName || 'this driver'} (optional):`);
    if (reason === null) return; // User cancelled
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/drivers/${driverId}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
      toast({ title: "Driver Rejected", description: "Driver has been rejected", variant: "destructive" });
      fetchDrivers();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to reject driver", variant: "destructive" });
    }
  };

  const handleDeleteDriver = async (driverId, driverName) => {
    if (!window.confirm(`⚠️ PERMANENTLY DELETE ${driverName || 'this driver'}?\n\nThis action CANNOT be undone. All driver data will be removed.`)) {
      return;
    }
    
    // Double confirmation for safety
    if (!window.confirm(`Are you absolutely sure? Type 'yes' to confirm deletion of ${driverName}.`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/drivers/${driverId}`);
      toast({ title: "Driver Deleted", description: `${driverName || 'Driver'} has been permanently removed` });
      fetchDrivers();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete driver", variant: "destructive" });
    }
  };

  const handleUpdatePayoutPercentage = async (driverId) => {
    try {
      const percentage = parseFloat(newPayoutPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        toast({ title: "Invalid Percentage", description: "Enter a value between 0 and 100", variant: "destructive" });
        return;
      }
      
      await axios.put(`${BACKEND_URL}/api/admin/drivers/${driverId}/payout?payout_percentage=${percentage}`);
      toast({ title: "Payout Updated", description: `Driver payout set to ${percentage}%` });
      setEditingDriverPayout(null);
      setNewPayoutPercentage('');
      fetchDrivers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to update payout", variant: "destructive" });
    }
  };

  const handleCreateVehicle = async () => {
    try {
      if (!newVehicle.name || newVehicle.base_fare <= 0) {
        toast({ title: "Missing Fields", description: "Name and base fare are required", variant: "destructive" });
        return;
      }
      
      await axios.post(`${BACKEND_URL}/api/admin/vehicles`, newVehicle);
      toast({ title: "Vehicle Created", description: `${newVehicle.name} has been added` });
      setIsAddingVehicle(false);
      setNewVehicle({
        name: '', description: '', image_url: '', service_types: ['transportation'],
        base_fare: 0, per_mile: 0, per_minute: 0, minimum_fare: 0, booking_fee: 2, per_stop_fee: 0, hourly_rate: 0, max_passengers: 4, is_active: true
      });
      fetchVehicles();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to create vehicle", variant: "destructive" });
    }
  };

  const handleUpdateVehicle = async (vehicleId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/vehicles/${vehicleId}`, editingVehicle);
      toast({ title: "Vehicle Updated", description: "Changes have been saved" });
      setEditingVehicle(null);
      fetchVehicles();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to update vehicle", variant: "destructive" });
    }
  };

  const handleDeleteVehicle = async (vehicleId, vehicleName) => {
    if (!window.confirm(`Delete ${vehicleName}? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/vehicles/${vehicleId}`);
      toast({ title: "Vehicle Deleted", description: `${vehicleName} has been removed` });
      fetchVehicles();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete vehicle", variant: "destructive" });
    }
  };

  // Toggle vehicle active/inactive status
  const handleToggleVehicleStatus = async (vehicleId, vehicleName, currentStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/vehicles/${vehicleId}`, { is_active: !currentStatus });
      toast({ 
        title: currentStatus ? "Vehicle Deactivated" : "Vehicle Activated", 
        description: `${vehicleName} is now ${currentStatus ? 'inactive' : 'active'}` 
      });
      fetchVehicles();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to update vehicle status", variant: "destructive" });
    }
  };

  const toggleServiceType = (vehicle, setVehicle, type) => {
    const types = vehicle.service_types || [];
    const newTypes = types.includes(type) 
      ? types.filter(t => t !== type)
      : [...types, type];
    setVehicle({ ...vehicle, service_types: newTypes.length > 0 ? newTypes : ['transportation'] });
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_id');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  // Payout handlers
  const handleProcessAllPayouts = async () => {
    if (!window.confirm(`Process payouts for ${pendingPayouts.driver_count} drivers totaling $${pendingPayouts.total_pending}?`)) return;
    
    setIsProcessingPayout(true);
    try {
      const driverIds = selectedDriversForPayout.length > 0 ? selectedDriversForPayout : null;
      const response = await axios.post(`${BACKEND_URL}/api/payouts/process`, { driver_ids: driverIds });
      toast({ 
        title: "Payouts Initiated!", 
        description: `$${response.data.total_amount} sent to ${response.data.driver_count} drivers via PayPal` 
      });
      setSelectedDriversForPayout([]);
      fetchPendingPayouts();
      fetchPayoutHistory();
    } catch (error) {
      toast({ 
        title: "Payout Failed", 
        description: error.response?.data?.detail || "Failed to process payouts", 
        variant: "destructive" 
      });
    } finally {
      setIsProcessingPayout(false);
    }
  };

  const handleProcessSinglePayout = async (driverId, driverName, amount) => {
    if (!window.confirm(`Send $${amount} payout to ${driverName}?`)) return;
    
    setIsProcessingPayout(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payouts/process-single/${driverId}`);
      toast({ 
        title: "Payout Sent!", 
        description: `$${response.data.total_amount} sent to ${driverName} via PayPal` 
      });
      fetchPendingPayouts();
      fetchPayoutHistory();
    } catch (error) {
      toast({ 
        title: "Payout Failed", 
        description: error.response?.data?.detail || "Failed to process payout", 
        variant: "destructive" 
      });
    } finally {
      setIsProcessingPayout(false);
    }
  };

  const handleUpdatePayoutSettings = async (newSettings) => {
    try {
      await axios.put(`${BACKEND_URL}/api/payouts/settings`, newSettings);
      toast({ title: "Settings Updated", description: "Payout settings have been saved" });
      setPayoutSettings(newSettings);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update settings", variant: "destructive" });
    }
  };

  const toggleDriverSelection = (driverId) => {
    setSelectedDriversForPayout(prev => 
      prev.includes(driverId) 
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId]
    );
  };

  const selectAllDrivers = () => {
    if (selectedDriversForPayout.length === pendingPayouts.drivers.length) {
      setSelectedDriversForPayout([]);
    } else {
      setSelectedDriversForPayout(pendingPayouts.drivers.map(d => d.driver_id));
    }
  };

  // Handle admin complete ride
  const [completingRideId, setCompletingRideId] = useState(null);
  const [reassigningRideId, setReassigningRideId] = useState(null);
  
  const handleCompleteRide = async (rideId) => {
    if (!window.confirm('Are you sure you want to mark this ride as completed? This will calculate driver earnings and free up the driver for new rides.')) {
      return;
    }
    
    setCompletingRideId(rideId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/rides/${rideId}/complete`);
      toast({ 
        title: "Ride Completed", 
        description: `Ride marked as completed. Driver earnings: $${response.data.driver_earnings || 0}` 
      });
      fetchRides();
      fetchStats();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to complete ride", 
        variant: "destructive" 
      });
    } finally {
      setCompletingRideId(null);
    }
  };

  const handleReassignRide = async (rideId) => {
    if (!window.confirm('Are you sure you want to reassign this ride? This will remove the current driver and put the ride back in the queue for a new driver.')) {
      return;
    }
    
    setReassigningRideId(rideId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/rides/${rideId}/reassign`);
      toast({ 
        title: "Ride Reassigned", 
        description: "Ride is back in the queue waiting for a new driver" 
      });
      fetchRides();
      fetchStats();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to reassign ride", 
        variant: "destructive" 
      });
    } finally {
      setReassigningRideId(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700',
      suspended: 'bg-gray-100 text-gray-700',
      completed: 'bg-green-100 text-green-700',
      searching_driver: 'bg-blue-100 text-blue-700',
      driver_assigned: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-cyan-100 text-cyan-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return config[status] || 'bg-gray-100 text-gray-700';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="h-16 w-16 text-amber-500 animate-spin" />
      </div>
    );
  }

  // Vehicle Form Component
  const VehicleForm = ({ vehicle, setVehicle, onSave, onCancel, isNew }) => (
    <Card className="p-6 border-2 border-amber-300">
      <h3 className="font-bold text-lg mb-4">{isNew ? 'Add New Vehicle' : 'Edit Vehicle'}</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Vehicle Name *</Label>
          <Input value={vehicle.name} onChange={(e) => setVehicle({...vehicle, name: e.target.value})} placeholder="e.g., Sedan, Large Sprinter, 26' Box Truck" className="mt-1" />
        </div>
        <div>
          <Label>Vehicle Image</Label>
          <div className="mt-1 space-y-2">
            {vehicle.image_url && (
              <div className="relative w-24 h-24 border rounded-lg overflow-hidden">
                <img src={vehicle.image_url.startsWith('/api') ? `${BACKEND_URL}${vehicle.image_url}` : vehicle.image_url} alt="Vehicle" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex gap-2">
              <input 
                type="file" 
                accept="image/*" 
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file && vehicle.id) {
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const response = await axios.post(`${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/upload-image`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      setVehicle({...vehicle, image_url: response.data.image_url});
                      toast({ title: "Image Uploaded", description: "Vehicle image updated successfully" });
                    } catch (error) {
                      toast({ title: "Upload Failed", description: error.response?.data?.detail || "Failed to upload image", variant: "destructive" });
                    }
                  } else if (!vehicle.id) {
                    toast({ title: "Save First", description: "Please save the vehicle first, then upload an image", variant: "destructive" });
                  }
                }}
                className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-amber-100 file:text-amber-700 hover:file:bg-amber-200"
              />
            </div>
            <p className="text-xs text-gray-500">Or enter URL:</p>
            <Input value={vehicle.image_url || ''} onChange={(e) => setVehicle({...vehicle, image_url: e.target.value})} placeholder="https://..." className="text-sm" />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Description</Label>
          <Textarea value={vehicle.description} onChange={(e) => setVehicle({...vehicle, description: e.target.value})} placeholder="Brief description of vehicle capacity and use" className="mt-1" rows={2} />
        </div>
        <div className="md:col-span-2">
          <Label>Service Types</Label>
          <div className="flex gap-3 mt-2">
            <button type="button" onClick={() => toggleServiceType(vehicle, setVehicle, 'transportation')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${vehicle.service_types?.includes('transportation') ? 'bg-amber-100 border-2 border-amber-500' : 'bg-gray-100 border-2 border-gray-200'}`}>
              <Car size={18} /> Transportation
            </button>
            <button type="button" onClick={() => toggleServiceType(vehicle, setVehicle, 'delivery')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${vehicle.service_types?.includes('delivery') ? 'bg-amber-100 border-2 border-amber-500' : 'bg-gray-100 border-2 border-gray-200'}`}>
              <Package size={18} /> Delivery
            </button>
          </div>
        </div>
        
        {/* Delivery-specific fields */}
        {vehicle.service_types?.includes('delivery') && (
          <>
            <div className="md:col-span-2 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <Package size={18} /> Delivery Specifications
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Max Boxes</Label>
                  <Input type="number" value={vehicle.max_boxes || ''} onChange={(e) => setVehicle({...vehicle, max_boxes: parseInt(e.target.value) || 0})} 
                    placeholder="e.g., 8" className="mt-1" />
                  <p className="text-xs text-gray-500 mt-1">Number of boxes</p>
                </div>
                <div>
                  <Label>Max Weight Per Box (lbs)</Label>
                  <Input type="number" value={vehicle.max_weight_per_box || ''} onChange={(e) => setVehicle({...vehicle, max_weight_per_box: parseInt(e.target.value) || 0})} 
                    placeholder="e.g., 30" className="mt-1" />
                  <p className="text-xs text-gray-500 mt-1">Weight limit per box</p>
                </div>
                <div>
                  <Label>Max Pallets</Label>
                  <Input type="number" value={vehicle.max_pallets || ''} onChange={(e) => setVehicle({...vehicle, max_pallets: parseInt(e.target.value) || 0})} 
                    placeholder="e.g., 3" className="mt-1" />
                  <p className="text-xs text-gray-500 mt-1">For larger vehicles</p>
                </div>
                <div>
                  <Label>Delivery Category</Label>
                  <select value={vehicle.delivery_category || ''} onChange={(e) => setVehicle({...vehicle, delivery_category: e.target.value})}
                    className="w-full mt-1 p-2 border-2 rounded-lg">
                    <option value="">Select category</option>
                    <option value="food">Food & Catering</option>
                    <option value="small_packages">Small Packages</option>
                    <option value="medium_freight">Medium Freight</option>
                    <option value="large_freight">Large Freight</option>
                    <option value="moving">Moving & Relocation</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label>Capacity Notes</Label>
                  <Input value={vehicle.capacity_notes || ''} onChange={(e) => setVehicle({...vehicle, capacity_notes: e.target.value})} 
                    placeholder="e.g., Can also use Med SUV, suitable for med moves" className="mt-1" />
                </div>
              </div>
            </div>
          </>
        )}

        <div>
          <Label>Base Fare ($) *</Label>
          <Input type="number" step="0.01" value={vehicle.base_fare} onChange={(e) => setVehicle({...vehicle, base_fare: parseFloat(e.target.value) || 0})} className="mt-1" />
        </div>
        <div>
          <Label>Per Mile ($)</Label>
          <Input type="number" step="0.01" value={vehicle.per_mile} onChange={(e) => setVehicle({...vehicle, per_mile: parseFloat(e.target.value) || 0})} className="mt-1" />
        </div>
        <div>
          <Label>Per Minute ($)</Label>
          <Input type="number" step="0.01" value={vehicle.per_minute} onChange={(e) => setVehicle({...vehicle, per_minute: parseFloat(e.target.value) || 0})} className="mt-1" />
        </div>
        <div>
          <Label>Minimum Fare ($)</Label>
          <Input type="number" step="0.01" value={vehicle.minimum_fare} onChange={(e) => setVehicle({...vehicle, minimum_fare: parseFloat(e.target.value) || 0})} className="mt-1" />
        </div>
        <div>
          <Label>Booking Fee ($)</Label>
          <Input type="number" step="0.01" value={vehicle.booking_fee} onChange={(e) => setVehicle({...vehicle, booking_fee: parseFloat(e.target.value) || 0})} className="mt-1" />
        </div>
        <div>
          <Label>Per Stop Fee ($)</Label>
          <Input type="number" step="0.01" value={vehicle.per_stop_fee} onChange={(e) => setVehicle({...vehicle, per_stop_fee: parseFloat(e.target.value) || 0})} className="mt-1" />
        </div>
        <div>
          <Label>Hourly Rate ($)</Label>
          <Input type="number" step="0.01" value={vehicle.hourly_rate || ''} onChange={(e) => setVehicle({...vehicle, hourly_rate: parseFloat(e.target.value) || 0})} 
            placeholder="e.g., 45" className="mt-1" />
        </div>
        <div>
          <Label>Max Passengers</Label>
          <Input type="number" value={vehicle.max_passengers} onChange={(e) => setVehicle({...vehicle, max_passengers: parseInt(e.target.value) || 1})} className="mt-1" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="is_active" checked={vehicle.is_active} onChange={(e) => setVehicle({...vehicle, is_active: e.target.checked})} />
          <Label htmlFor="is_active">Active</Label>
        </div>
      </div>
      <div className="flex gap-2 mt-6">
        <Button onClick={onSave} className="bg-green-600 hover:bg-green-700">Save</Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Admin Driver Edit Modal */}
      <AdminDriverEdit
        isOpen={!!editingDriverId}
        onClose={() => setEditingDriverId(null)}
        driverId={editingDriverId}
        backendUrl={BACKEND_URL}
        onUpdate={fetchDrivers}
      />

      {/* Password Reset Modal */}
      {passwordResetModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Key className="text-amber-600" /> Reset Password
            </h2>
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Resetting password for:</p>
                <p className="font-bold">{passwordResetModal.name}</p>
                <p className="text-sm text-gray-500">{passwordResetModal.email}</p>
                <Badge className="mt-1">{passwordResetModal.userType}</Badge>
              </div>
              
              <div>
                <Label className="block text-sm font-medium mb-1">New Password *</Label>
                <Input
                  type="text"
                  placeholder="Enter new password (min 6 characters)"
                  value={newPasswordInput}
                  onChange={(e) => setNewPasswordInput(e.target.value)}
                  data-testid="admin-reset-password-input"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendNotification"
                  checked={sendPasswordNotification}
                  onChange={(e) => setSendPasswordNotification(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="sendNotification" className="text-sm cursor-pointer">
                  Send SMS notification to {passwordResetModal.userType} with new password
                </Label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setPasswordResetModal({ show: false, email: '', userType: '', name: '' })}
                  className="flex-1"
                  disabled={isResettingPassword}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAdminPasswordReset}
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  disabled={isResettingPassword || newPasswordInput.length < 6}
                  data-testid="admin-reset-password-confirm"
                >
                  {isResettingPassword ? <Loader2 className="animate-spin mr-2" size={16} /> : <Key size={16} className="mr-2" />}
                  Reset Password
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Button variant="ghost" onClick={() => navigate('/')} className="flex-shrink-0">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3 min-w-0">
              <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain flex-shrink-0" />
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent whitespace-nowrap">J-Style Admin</span>
            </div>
            {/* Pending Rides Alert in Header */}
            {pendingRides.total > 0 && (
              <Button 
                onClick={() => setActiveTab('dispatch')}
                className="bg-red-600 hover:bg-red-700 animate-pulse ml-4"
              >
                <AlertCircle className="mr-2" size={18} />
                🚨 {pendingRides.total} PENDING
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button variant="ghost" onClick={fetchAllData} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" onClick={handleLogout}>Logout</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: Activity },
            { id: 'dispatch', label: pendingRides.total > 0 ? `🚨 DISPATCH (${pendingRides.total})` : 'Dispatch', icon: Send },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'drivers', label: 'Drivers', icon: Truck },
            { id: 'vehicles', label: 'Vehicles', icon: Car },
            { id: 'rides', label: 'Rides', icon: MapPin },
            { id: 'payouts', label: 'Payouts', icon: DollarSign },
            { id: 'tax1099', label: '1099 Tax', icon: FileText },
            { id: 'referrals', label: 'Referrals', icon: Gift },
            { id: 'tips', label: 'Tips', icon: Gift },
            { id: 'promos', label: 'Promo Codes', icon: Tag },
            { id: 'terms', label: 'Terms', icon: FileText },
            { id: 'website', label: 'Website', icon: Globe },
          ].map(tab => (
            <Button key={tab.id} variant={activeTab === tab.id ? 'default' : 'outline'} onClick={() => { setActiveTab(tab.id); if (tab.id === 'users') fetchUsers(); if (tab.id === 'tax1099') fetch1099Data(); if (tab.id === 'referrals') { fetchReferralData(); fetchDriverReferralData(); fetchDriverReferralPayouts(); } if (tab.id === 'dispatch') fetchDispatchData(); if (tab.id === 'website') fetchCMSContent(); }}
              className={activeTab === tab.id ? 'bg-amber-500 hover:bg-amber-600' : ''} data-testid={`tab-${tab.id}`}>
              <tab.icon size={18} className="mr-2" />{tab.label}
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6 border-2" data-testid="stat-total-drivers">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-2">
                  <Users className="text-slate-700" size={24} />
                </div>
                <p className="text-3xl font-bold">{stats.drivers?.total || 0}</p>
                <p className="text-sm font-medium text-gray-700">Total Drivers</p>
                <p className="text-xs text-green-600">{stats.drivers?.online || 0} online</p>
              </Card>
              
              <Card className="p-6 border-2 border-amber-200 bg-amber-50">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-2">
                  <Clock className="text-amber-600" size={24} />
                </div>
                <p className="text-3xl font-bold">{stats.drivers?.pending || 0}</p>
                <p className="text-sm font-medium text-gray-700">Pending Approvals</p>
              </Card>
              
              <Card className="p-6 border-2">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-2">
                  <Car className="text-green-600" size={24} />
                </div>
                <p className="text-3xl font-bold">{stats.rides?.total || 0}</p>
                <p className="text-sm font-medium text-gray-700">Total Rides</p>
                <p className="text-xs text-blue-600">{stats.rides?.active || 0} active</p>
              </Card>
              
              <Card className="p-6 border-2">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-2">
                  <DollarSign className="text-yellow-600" size={24} />
                </div>
                <p className="text-3xl font-bold">${stats.financials?.total_revenue || 0}</p>
                <p className="text-sm font-medium text-gray-700">Total Revenue</p>
              </Card>
            </div>

            {/* URGENT: Pending Rides Alert */}
            {pendingRides.total > 0 && (
              <Card className="p-6 border-4 border-red-500 bg-red-50 animate-pulse mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                      <AlertCircle className="text-white" size={32} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-red-700">🚨 {pendingRides.total} RIDER{pendingRides.total > 1 ? 'S' : ''} WAITING!</h3>
                      <p className="text-red-600 font-medium">
                        {pendingRides.urgent_count > 0 
                          ? `${pendingRides.urgent_count} waiting over 5 minutes - URGENT DISPATCH NEEDED`
                          : 'Riders need to be assigned a driver'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="lg"
                    onClick={() => setActiveTab('dispatch')}
                    className="bg-red-600 hover:bg-red-700 text-lg px-8 py-6"
                  >
                    <Send className="mr-2" size={24} /> DISPATCH NOW
                  </Button>
                </div>
              </Card>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 border-2">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <AlertCircle className="text-amber-500" size={20} />Pending Approvals
                </h3>
                {drivers.filter(d => d.status === 'pending').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No pending approvals</p>
                ) : (
                  <div className="space-y-3">
                    {drivers.filter(d => d.status === 'pending').slice(0, 5).map(driver => (
                      <div key={driver.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{driver.name}</p>
                          <p className="text-sm text-gray-500">{driver.email}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleApproveDriver(driver.id)}>
                            <CheckCircle size={14} className="mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleRejectDriver(driver.id, driver.name)}>
                            <XCircle size={14} className="mr-1" />Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card className="p-6 border-2">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Truck className="text-blue-500" size={20} />Vehicle Types
                </h3>
                <div className="space-y-2">
                  {vehicles.slice(0, 4).map(v => (
                    <div key={v.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {v.image_url && <img src={v.image_url} alt={v.name} className="w-10 h-10 rounded object-cover" />}
                        <span className="font-medium">{v.name}</span>
                      </div>
                      <span className="text-green-600 font-bold">${v.base_fare}</span>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-4" onClick={() => setActiveTab('vehicles')}>
                  Manage Vehicles
                </Button>
              </Card>
            </div>
          </div>
        )}

        {/* Dispatch Tab - Pending Rides & Active Rides Tracking */}
        {activeTab === 'dispatch' && (
          <div className="space-y-6">
            {/* Sub-tabs */}
            <div className="flex gap-2 mb-4">
              <Button 
                variant={dispatchTab === 'pending' ? 'default' : 'outline'}
                onClick={() => setDispatchTab('pending')}
                className={dispatchTab === 'pending' ? 'bg-red-500 hover:bg-red-600' : ''}
              >
                <AlertCircle size={18} className="mr-2" />
                Pending Rides {pendingRides.urgent_count > 0 && <Badge className="ml-2 bg-white text-red-600">{pendingRides.urgent_count} urgent</Badge>}
              </Button>
              <Button 
                variant={dispatchTab === 'active' ? 'default' : 'outline'}
                onClick={() => setDispatchTab('active')}
                className={dispatchTab === 'active' ? 'bg-green-500 hover:bg-green-600' : ''}
              >
                <Car size={18} className="mr-2" />
                Active Rides ({activeRidesData.total})
              </Button>
              <Button 
                variant={dispatchTab === 'unpaid' ? 'default' : 'outline'}
                onClick={() => setDispatchTab('unpaid')}
                className={dispatchTab === 'unpaid' ? 'bg-amber-500 hover:bg-amber-600' : ''}
              >
                <DollarSign size={18} className="mr-2" />
                Unpaid Rides {unpaidRides.total > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800">{unpaidRides.total}</Badge>}
              </Button>
              <Button variant="outline" onClick={fetchDispatchData} disabled={isLoadingDispatch}>
                <RefreshCw size={18} className={isLoadingDispatch ? 'animate-spin' : ''} />
              </Button>
            </div>

            {/* Pending Rides - Riders waiting for drivers */}
            {dispatchTab === 'pending' && (
              <Card className="p-6 border-2 border-red-200">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <AlertCircle className="text-red-500" /> Riders Waiting for Drivers
                </h2>
                
                {pendingRides.rides.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No pending rides. All riders have been matched!</p>
                ) : (
                  <div className="space-y-4">
                    {pendingRides.rides.map((ride) => (
                      <Card key={ride.id} className={`p-4 border-2 ${ride.is_urgent ? 'border-red-400 bg-red-50' : 'border-amber-200'}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-bold">{ride.passenger_name}</span>
                              {ride.is_urgent && <Badge className="bg-red-500">URGENT - {ride.wait_minutes} min waiting</Badge>}
                              {!ride.is_urgent && <Badge variant="outline">{ride.wait_minutes} min</Badge>}
                            </div>
                            <p className="text-sm text-gray-600"><strong>Pickup:</strong> {ride.pickup?.address || ride.pickup}</p>
                            <p className="text-sm text-gray-600"><strong>Dropoff:</strong> {ride.dropoff?.address || ride.dropoff}</p>
                            <p className="text-sm text-gray-500">Vehicle: {ride.vehicle_type} | Est. ${ride.estimated_price}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => fetchNearbyDriversForDispatch(ride.id)}
                              className="border-blue-300 text-blue-600"
                            >
                              <Search size={16} className="mr-1" /> Find Drivers
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => notifyNearbyDrivers(ride.id)}
                              className="bg-amber-500 hover:bg-amber-600"
                            >
                              <Send size={16} className="mr-1" /> Alert Drivers
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => deleteRide(ride.id, ride.passenger_name || 'Unknown')}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={16} className="mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Active Rides - Drivers currently on trips */}
            {dispatchTab === 'active' && (
              <Card className="p-6 border-2 border-green-200">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Truck className="text-green-500" /> Drivers on Active Rides
                </h2>
                
                {/* Status summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card className="p-4 bg-blue-50 border-blue-200">
                    <p className="text-sm text-blue-600">En Route to Pickup</p>
                    <p className="text-2xl font-bold text-blue-700">{activeRidesData.counts?.en_route_to_pickup || 0}</p>
                  </Card>
                  <Card className="p-4 bg-amber-50 border-amber-200">
                    <p className="text-sm text-amber-600">At Pickup Location</p>
                    <p className="text-2xl font-bold text-amber-700">{activeRidesData.counts?.at_pickup || 0}</p>
                  </Card>
                  <Card className="p-4 bg-green-50 border-green-200">
                    <p className="text-sm text-green-600">In Progress (Delivering)</p>
                    <p className="text-2xl font-bold text-green-700">{activeRidesData.counts?.in_progress || 0}</p>
                  </Card>
                </div>
                
                {activeRidesData.rides.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No active rides at the moment.</p>
                ) : (
                  <div className="space-y-4">
                    {activeRidesData.rides.map((ride) => (
                      <Card key={ride.id} className="p-4 border-2">
                        <div className="flex gap-4">
                          {/* Driver Info */}
                          <div className="flex items-center gap-3">
                            {ride.driver?.profile_photo ? (
                              <img 
                                src={ride.driver.profile_photo.startsWith('http') ? ride.driver.profile_photo : `${BACKEND_URL}${ride.driver.profile_photo}`}
                                alt={ride.driver?.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-amber-400"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                                <User className="text-amber-600" size={24} />
                              </div>
                            )}
                            <div>
                              <p className="font-bold">{ride.driver?.name || 'Unknown Driver'}</p>
                              <p className="text-sm text-gray-500">{ride.driver?.vehicle_model} • {ride.driver?.license_plate}</p>
                              <p className="text-xs text-gray-400">{ride.driver?.phone}</p>
                            </div>
                          </div>
                          
                          {/* Ride Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={
                                ride.status === 'driver_assigned' ? 'bg-blue-500' :
                                ride.status === 'arrived' ? 'bg-amber-500' :
                                'bg-green-500'
                              }>
                                {ride.status === 'driver_assigned' ? 'En Route to Pickup' :
                                 ride.status === 'arrived' ? 'At Pickup' :
                                 'In Progress'}
                              </Badge>
                              <span className="text-sm text-gray-500">{ride.passenger_name}</span>
                            </div>
                            <p className="text-sm"><strong>From:</strong> {ride.pickup?.address || ride.pickup}</p>
                            <p className="text-sm"><strong>To:</strong> {ride.dropoff?.address || ride.dropoff}</p>
                          </div>
                          
                          {/* Price & Actions */}
                          <div className="text-right flex flex-col items-end gap-2">
                            <p className="text-lg font-bold text-green-600">${ride.estimated_price}</p>
                            <p className="text-xs text-gray-500">{ride.vehicle_type}</p>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              onClick={() => fetchNearbyDriversForDispatch(ride.id)}
                            >
                              <RefreshCw size={14} className="mr-1" /> Change Driver
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Unpaid Rides - Need manual payment verification */}
            {dispatchTab === 'unpaid' && (
              <Card className="p-6 border-2 border-amber-200">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <DollarSign className="text-amber-500" /> Unpaid Rides (Payment Pending)
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  These rides show as unpaid in the system. If payment was completed in Stripe but the webhook failed, 
                  you can manually mark them as paid to move them to dispatch.
                </p>
                
                {unpaidRides.rides.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No unpaid rides. All payments are processed!</p>
                ) : (
                  <div className="space-y-4">
                    {unpaidRides.rides.map((ride) => (
                      <Card key={ride.id} className="p-4 border-2 border-amber-200 bg-amber-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-bold">{ride.passenger_name || 'Unknown'}</span>
                              <Badge className="bg-amber-500">Payment Pending</Badge>
                              {ride.has_scheduled_time && (
                                <Badge variant="outline" className="border-blue-400 text-blue-600">
                                  Scheduled: {new Date(ride.scheduled_pickup_time || ride.scheduled_time).toLocaleString()}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600"><strong>Pickup:</strong> {ride.pickup?.address || 'N/A'}</p>
                            <p className="text-sm text-gray-600"><strong>Dropoff:</strong> {ride.dropoff?.address || 'N/A'}</p>
                            <p className="text-sm text-gray-500">
                              Vehicle: {ride.vehicle_type} | Est. ${ride.estimated_price} | 
                              Created: {new Date(ride.created_at).toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">Ride ID: {ride.id}</p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => markRideAsPaid(ride.id, ride.passenger_name || 'Unknown')}
                            >
                              <CheckCircle size={16} className="mr-1" />
                              Mark as Paid
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() => deleteRide(ride.id, ride.passenger_name || 'Unknown')}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Delete Ride
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Nearby Drivers Modal */}
            {selectedRideForDispatch && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold">Assign Driver to Ride</h3>
                      <p className="text-sm text-gray-500">Rider: {selectedRideForDispatch.passenger_name} • Waiting {selectedRideForDispatch.wait_minutes} min</p>
                    </div>
                    <Button variant="ghost" onClick={() => { setSelectedRideForDispatch(null); setNearbyDriversForDispatch([]); }}>
                      <X size={20} />
                    </Button>
                  </div>
                  
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm"><strong>Pickup:</strong> {typeof selectedRideForDispatch.pickup === 'object' ? selectedRideForDispatch.pickup?.address : selectedRideForDispatch.pickup}</p>
                    <p className="text-sm"><strong>Dropoff:</strong> {typeof selectedRideForDispatch.dropoff === 'object' ? selectedRideForDispatch.dropoff?.address : selectedRideForDispatch.dropoff}</p>
                  </div>
                  
                  <h4 className="font-bold mb-3">Nearby Available Drivers ({nearbyDriversForDispatch.filter(d => !d.has_active_ride).length})</h4>
                  
                  {nearbyDriversForDispatch.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No online drivers nearby. Try expanding the search radius.</p>
                  ) : (
                    <div className="space-y-3">
                      {nearbyDriversForDispatch.map((driver) => (
                        <Card key={driver.id} className={`p-3 border ${driver.has_active_ride ? 'bg-gray-100 opacity-60' : driver.is_online ? 'hover:border-green-400 border-green-200' : 'hover:border-amber-400'}`}>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              {driver.profile_photo ? (
                                <img 
                                  src={driver.profile_photo.startsWith('http') ? driver.profile_photo : `${BACKEND_URL}${driver.profile_photo}`}
                                  alt={driver.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                                  <User className="text-amber-600" size={20} />
                                </div>
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-bold">{driver.name}</p>
                                  {driver.is_online ? (
                                    <Badge className="bg-green-500 text-xs">Online</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-400 text-xs">Offline</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{driver.vehicle_type || driver.vehicle_model} • ⭐ {driver.rating}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                {driver.distance_miles !== null ? (
                                  <>
                                    <p className="font-bold text-blue-600">{driver.distance_miles} mi</p>
                                    <p className="text-xs text-gray-500">~{driver.eta_minutes} min ETA</p>
                                  </>
                                ) : (
                                  <p className="text-xs text-gray-400">Location unknown</p>
                                )}
                              </div>
                              {driver.has_active_ride ? (
                                <Badge variant="outline" className="text-gray-500">On Trip</Badge>
                              ) : (
                                <Button 
                                  size="sm"
                                  onClick={() => assignDriverFromDispatch(selectedRideForDispatch.id, driver.id, driver.name)}
                                  disabled={isAssigningDriverDispatch}
                                  className="bg-green-500 hover:bg-green-600"
                                >
                                  {isAssigningDriverDispatch ? <Loader2 className="animate-spin" size={16} /> : 'Assign'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard backendUrl={BACKEND_URL} />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card className="p-6 border-2" data-testid="users-tab">
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <Input 
                  placeholder="Search users by name, email, or phone..." 
                  className="pl-10 border-2" 
                  value={userSearch} 
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                />
              </div>
              <Button onClick={fetchUsers} className="bg-amber-500 hover:bg-amber-600">
                <Search size={18} className="mr-2" />Search
              </Button>
              <Button variant="outline" onClick={fetchUsers} className="border-2">
                <RefreshCw size={18} className="mr-2" />Refresh
              </Button>
              <Button onClick={() => setIsAddingUser(true)} className="bg-green-600 hover:bg-green-700">
                <UserPlus size={18} className="mr-2" />Add User
              </Button>
            </div>

            {/* Add User Modal */}
            {isAddingUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="w-full max-w-md p-6 m-4">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <UserPlus className="text-green-600" /> Add New User
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Full Name *</label>
                      <Input
                        placeholder="e.g., John Smith"
                        value={newUser.full_name}
                        onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                        data-testid="new-user-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <Input
                        type="email"
                        placeholder="e.g., john@example.com"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        data-testid="new-user-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone *</label>
                      <Input
                        placeholder="e.g., 5551234567"
                        value={newUser.phone}
                        onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                        data-testid="new-user-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Temporary Password</label>
                      <Input
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        data-testid="new-user-password"
                      />
                      <p className="text-xs text-gray-500 mt-1">User can change this after first login</p>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => { setIsAddingUser(false); setNewUser({ full_name: '', email: '', phone: '', password: 'Welcome123!' }); }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateUser}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid="create-user-btn"
                      >
                        Create User
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {isLoadingUsers ? (
              <div className="text-center py-12">
                <Loader2 className="animate-spin mx-auto mb-4" size={32} />
                <p>Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users size={48} className="mx-auto mb-4 opacity-50" />
                <p>No users found</p>
                <p className="text-sm">Users will appear here when they sign up</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">Total Users: <strong>{users.length}</strong></p>
                
                {users.map(user => (
                  <Card key={user.id} className="p-4 border-2 hover:border-amber-300 transition-colors" data-testid={`user-card-${user.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="text-blue-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{user.full_name || 'No Name'}</h3>
                          <p className="text-gray-600 text-sm">{user.email}</p>
                          <p className="text-gray-500 text-sm">{user.phone || 'No phone'}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500">Total Rides</p>
                          <p className="font-bold text-lg">{user.total_rides || 0}</p>
                        </div>
                        <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500">Joined</p>
                          <p className="font-medium text-sm">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500">Last Ride</p>
                          <p className="font-medium text-sm">
                            {user.last_ride ? new Date(user.last_ride).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => fetchUserDetails(user.id)}
                            className="border-blue-300 text-blue-600 hover:bg-blue-50"
                          >
                            View Details
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => openPasswordResetModal(user.email, 'user', user.full_name || user.email)}
                            className="border-amber-300 text-amber-600 hover:bg-amber-50"
                            data-testid={`reset-password-user-${user.id}`}
                          >
                            <Key size={16} className="mr-1" /> Reset PW
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDeleteUser(user.id, user.full_name)}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stripe Info */}
                    {user.stripe_customer_id && (
                      <div className="mt-3 pt-3 border-t text-sm text-gray-500">
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Payment Method Saved</span>
                        {user.card_last4 && <span className="ml-2">•••• {user.card_last4}</span>}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}

            {/* User Details Modal */}
            {selectedUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-bold">{selectedUser.user?.full_name || 'User Details'}</h2>
                      <p className="text-gray-600">{selectedUser.user?.email}</p>
                    </div>
                    <Button variant="ghost" onClick={() => setSelectedUser(null)}>
                      <XCircle size={24} />
                    </Button>
                  </div>

                  {/* User Info */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-medium">{selectedUser.user?.phone || 'Not provided'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Member Since</p>
                      <p className="font-medium">
                        {selectedUser.user?.created_at ? new Date(selectedUser.user.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600">Total Rides</p>
                      <p className="font-bold text-2xl text-blue-700">{selectedUser.stats?.total_rides || 0}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-600">Total Spent</p>
                      <p className="font-bold text-2xl text-green-700">${selectedUser.stats?.total_spent || 0}</p>
                    </div>
                  </div>

                  {/* Stripe Info */}
                  {selectedUser.user?.stripe_customer_id && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold mb-2">Payment Information</h3>
                      <p className="text-sm text-gray-600">Stripe Customer ID: {selectedUser.user.stripe_customer_id}</p>
                      {selectedUser.user.card_brand && selectedUser.user.card_last4 && (
                        <p className="text-sm text-gray-600">{selectedUser.user.card_brand} •••• {selectedUser.user.card_last4}</p>
                      )}
                    </div>
                  )}

                  {/* Recent Rides */}
                  <div>
                    <h3 className="font-semibold mb-3">Recent Rides</h3>
                    {selectedUser.rides?.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {selectedUser.rides.map(ride => (
                          <div key={ride.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{ride.pickup?.address?.slice(0, 30)}...</p>
                                <p className="text-gray-500">→ {ride.dropoff?.address?.slice(0, 30)}...</p>
                              </div>
                              <div className="text-right">
                                <Badge className={
                                  ride.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  ride.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                  'bg-blue-100 text-blue-700'
                                }>{ride.status}</Badge>
                                <p className="font-medium mt-1">${ride.estimated_price || 0}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {ride.created_at ? new Date(ride.created_at).toLocaleString() : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No rides yet</p>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={() => setSelectedUser(null)}>Close</Button>
                    <Button 
                      variant="destructive"
                      onClick={() => {
                        handleDeleteUser(selectedUser.user?.id, selectedUser.user?.full_name);
                      }}
                    >
                      <Trash2 size={16} className="mr-2" />Delete User
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </Card>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <div className="space-y-6">
            {/* Live Drivers Map */}
            <LiveDriversMap 
              backendUrl={BACKEND_URL}
              showTitle={true}
              height="h-80"
              autoRefresh={true}
              refreshInterval={15000}
            />
            
            {/* Driver List */}
            <Card className="p-6 border-2">
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                  <Input placeholder="Search drivers..." className="pl-10 border-2" value={driverSearch} onChange={(e) => setDriverSearch(e.target.value)} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {['all', 'pending', 'approved', 'rejected'].map(status => (
                    <Button key={status} variant={driverFilter === status ? 'default' : 'outline'} onClick={() => setDriverFilter(status)}
                      className={driverFilter === status ? 'bg-amber-500 hover:bg-amber-600' : 'border-2'} size="sm">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

            <div className="space-y-4">
              {drivers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users size={48} className="mx-auto mb-4 opacity-50" /><p>No drivers found</p>
                </div>
              ) : (
                drivers.map((driver) => (
                  <Card key={driver.id} className="p-6 hover:shadow-md transition-shadow border" data-testid={`driver-card-${driver.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-[250px]">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold">{driver.name}</h3>
                          <Badge className={getStatusBadge(driver.status)}>{driver.status}</Badge>
                          {driver.service_types && (
                            <div className="flex gap-1">
                              {driver.service_types.includes('transportation') && <Badge variant="outline" className="text-xs"><Car size={10} className="mr-1" />Transport</Badge>}
                              {driver.service_types.includes('delivery') && <Badge variant="outline" className="text-xs"><Package size={10} className="mr-1" />Delivery</Badge>}
                            </div>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
                          <p>📧 {driver.email}</p>
                          <p>📱 {driver.phone}</p>
                          <p>🚗 {driver.vehicle_model}</p>
                          <p>🔢 {driver.license_plate}</p>
                          {driver.status === 'approved' && (
                            <>
                              <p>🚘 {driver.total_rides || 0} rides</p>
                              <p>⭐ {driver.rating || 'N/A'}</p>
                              <p>💰 ${(driver.earnings || 0).toFixed(2)} earned</p>
                              <p className="flex items-center gap-1">
                                <Percent size={12} />
                                Payout: {driver.payout_percentage || 80}%
                                <button onClick={() => { setEditingDriverPayout(driver.id); setNewPayoutPercentage(driver.payout_percentage || 80); }}
                                  className="ml-1 text-amber-600 hover:text-amber-700">
                                  <Edit2 size={12} />
                                </button>
                              </p>
                            </>
                          )}
                        </div>
                        
                        {/* Payout Percentage Editor */}
                        {editingDriverPayout === driver.id && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg flex items-center gap-2">
                            <Label className="whitespace-nowrap">Payout %:</Label>
                            <Input type="number" min="0" max="100" value={newPayoutPercentage} onChange={(e) => setNewPayoutPercentage(e.target.value)}
                              className="w-20" />
                            <Button size="sm" onClick={() => handleUpdatePayoutPercentage(driver.id)} className="bg-green-600 hover:bg-green-700">Save</Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingDriverPayout(null)}>Cancel</Button>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/* Edit Button - Always visible */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setEditingDriverId(driver.id)}
                          className="text-purple-600 border-purple-300 hover:bg-purple-50 border-2"
                        >
                          <Edit size={16} className="mr-1" />Edit
                        </Button>
                        {/* Reset Password Button */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openPasswordResetModal(driver.email, 'driver', driver.name)}
                          className="text-amber-600 border-amber-300 hover:bg-amber-50 border-2"
                          data-testid={`reset-password-driver-${driver.id}`}
                        >
                          <Key size={16} className="mr-1" />Reset PW
                        </Button>
                        {driver.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => handleApproveDriver(driver.id)} className="text-green-600 border-green-300 hover:bg-green-50 border-2">
                            <CheckCircle size={16} className="mr-1" />Approve
                          </Button>
                        )}
                        {driver.status !== 'rejected' && (
                          <Button size="sm" variant="outline" onClick={() => handleRejectDriver(driver.id, driver.name)} className="text-red-600 border-red-300 hover:bg-red-50 border-2">
                            <XCircle size={16} className="mr-1" />Reject
                          </Button>
                        )}
                        {/* Delete Button - Always visible with confirmation */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleDeleteDriver(driver.id, driver.name)} 
                          className="text-red-700 border-red-500 hover:bg-red-100 border-2"
                          title="Permanently delete driver"
                        >
                          <Trash2 size={16} className="mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </Card>
          </div>
        )}

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Vehicle Management</h2>
              {!isAddingVehicle && (
                <Button onClick={() => setIsAddingVehicle(true)} className="bg-amber-500 hover:bg-amber-600">
                  <Plus size={18} className="mr-2" />Add Vehicle
                </Button>
              )}
            </div>

            {/* Vehicle Order Management */}
            <Card className="p-6 border-2 border-dashed border-amber-300 bg-amber-50/30">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                <Settings className="text-amber-500" size={20} />
                Vehicle Display Order
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Set the order vehicles appear in for users. Lower numbers appear first.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Ride Tab Order */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Car size={16} className="text-blue-500" />
                    Ride Tab Order
                  </h4>
                  <div className="space-y-2">
                    {vehicles
                      .filter(v => v.service_types?.includes('transportation') && v.is_active)
                      .sort((a, b) => (a.display_order_ride || 100) - (b.display_order_ride || 100))
                      .map((vehicle, idx) => (
                        <div key={vehicle.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                          <Input
                            type="number"
                            min="1"
                            value={vehicle.display_order_ride || idx + 1}
                            onChange={async (e) => {
                              const newOrder = parseInt(e.target.value) || 1;
                              try {
                                await axios.put(`${BACKEND_URL}/api/admin/vehicles/${vehicle.id}`, {
                                  display_order_ride: newOrder
                                });
                                fetchVehicles();
                              } catch (err) {
                                console.error('Error updating order:', err);
                              }
                            }}
                            className="w-16 text-center"
                          />
                          <span className="flex-1 truncate">{vehicle.name}</span>
                        </div>
                      ))}
                  </div>
                </div>
                
                {/* Delivery Tab Order */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Package size={16} className="text-green-500" />
                    Delivery Tab Order
                  </h4>
                  <div className="space-y-2">
                    {vehicles
                      .filter(v => v.service_types?.includes('delivery') && v.is_active)
                      .sort((a, b) => (a.display_order_delivery || 100) - (b.display_order_delivery || 100))
                      .map((vehicle, idx) => (
                        <div key={vehicle.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                          <Input
                            type="number"
                            min="1"
                            value={vehicle.display_order_delivery || idx + 1}
                            onChange={async (e) => {
                              const newOrder = parseInt(e.target.value) || 1;
                              try {
                                await axios.put(`${BACKEND_URL}/api/admin/vehicles/${vehicle.id}`, {
                                  display_order_delivery: newOrder
                                });
                                fetchVehicles();
                              } catch (err) {
                                console.error('Error updating order:', err);
                              }
                            }}
                            className="w-16 text-center"
                          />
                          <span className="flex-1 truncate">{vehicle.name}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </Card>

            {isAddingVehicle && (
              <VehicleForm vehicle={newVehicle} setVehicle={setNewVehicle} onSave={handleCreateVehicle}
                onCancel={() => setIsAddingVehicle(false)} isNew={true} />
            )}
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => (
                editingVehicle?.id === vehicle.id ? (
                  <VehicleForm key={vehicle.id} vehicle={editingVehicle} setVehicle={setEditingVehicle}
                    onSave={() => handleUpdateVehicle(vehicle.id)} onCancel={() => setEditingVehicle(null)} isNew={false} />
                ) : (
                  <Card key={vehicle.id} className={`p-6 border-2 ${!vehicle.is_active ? 'opacity-60 bg-gray-50' : ''}`} data-testid={`vehicle-${vehicle.id}`}>
                    {vehicle.image_url && (
                      <img src={vehicle.image_url} alt={vehicle.name} className="w-full h-40 object-cover rounded-lg mb-4" />
                    )}
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-bold">{vehicle.name}</h3>
                      <Badge variant={vehicle.is_active ? "default" : "secondary"} className={vehicle.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}>
                        {vehicle.is_active ? '✓ Active' : '○ Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{vehicle.description || 'No description'}</p>
                    
                    <div className="flex gap-2 mb-3">
                      {vehicle.service_types?.map(type => (
                        <Badge key={type} variant="outline" className="text-xs">
                          {type === 'transportation' ? <Car size={10} className="mr-1" /> : <Package size={10} className="mr-1" />}
                          {type}
                        </Badge>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                      <div className="flex justify-between"><span className="text-gray-600">Base Fare</span><span className="font-bold">${vehicle.base_fare}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Per Mile</span><span className="font-bold">${vehicle.per_mile}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Per Minute</span><span className="font-bold">${vehicle.per_minute}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Min Fare</span><span className="font-bold">${vehicle.minimum_fare}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Booking Fee</span><span className="font-bold">${vehicle.booking_fee}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Per Stop</span><span className="font-bold text-amber-600">${vehicle.per_stop_fee}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Hourly Rate</span><span className="font-bold text-blue-600">${vehicle.hourly_rate || 0}/hr</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Max Passengers</span><span className="font-bold">{vehicle.max_passengers}</span></div>
                    </div>

                    {/* Delivery Specifications */}
                    {vehicle.service_types?.includes('delivery') && (vehicle.max_boxes > 0 || vehicle.max_pallets > 0) && (
                      <div className="p-3 bg-blue-50 rounded-lg mb-4 text-sm">
                        <p className="font-semibold text-blue-800 mb-2 flex items-center gap-1">
                          <Package size={14} /> Delivery Specs
                        </p>
                        <div className="space-y-1 text-gray-700">
                          {vehicle.max_boxes > 0 && (
                            <p>Max Boxes: <span className="font-bold">{vehicle.max_boxes}</span> {vehicle.max_weight_per_box > 0 && `(${vehicle.max_weight_per_box} lbs each)`}</p>
                          )}
                          {vehicle.max_pallets > 0 && (
                            <p>Max Pallets: <span className="font-bold">{vehicle.max_pallets}</span></p>
                          )}
                          {vehicle.delivery_category && (
                            <p>Category: <span className="font-bold capitalize">{vehicle.delivery_category.replace('_', ' ')}</span></p>
                          )}
                          {vehicle.capacity_notes && (
                            <p className="text-xs text-gray-500 italic">{vehicle.capacity_notes}</p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button 
                        variant={vehicle.is_active ? "outline" : "default"}
                        className={vehicle.is_active ? "flex-1 text-orange-600 border-orange-300 hover:bg-orange-50" : "flex-1 bg-green-600 hover:bg-green-700"}
                        onClick={() => handleToggleVehicleStatus(vehicle.id, vehicle.name, vehicle.is_active)}
                        data-testid={`toggle-vehicle-${vehicle.id}`}
                      >
                        {vehicle.is_active ? (
                          <><ToggleRight size={14} className="mr-1" />Deactivate</>
                        ) : (
                          <><ToggleLeft size={14} className="mr-1" />Activate</>
                        )}
                      </Button>
                      <Button variant="outline" className="flex-1" onClick={() => setEditingVehicle({...vehicle})}>
                        <Edit2 size={14} className="mr-1" />Edit
                      </Button>
                      <Button variant="outline" className="text-red-600 border-red-300" onClick={() => handleDeleteVehicle(vehicle.id, vehicle.name)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Card>
                )
              ))}
            </div>
          </div>
        )}

        {/* Rides Tab */}
        {activeTab === 'rides' && (
          <div className="space-y-6">
            {/* Unassigned Scheduled Rides Alert Section */}
            {unassignedRides.total > 0 && (
              <Card className={`p-4 border-2 ${unassignedRides.urgent_count > 0 ? 'border-red-400 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-bold flex items-center gap-2 ${unassignedRides.urgent_count > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                    <AlertCircle size={20} className={unassignedRides.urgent_count > 0 ? 'text-red-500' : 'text-amber-500'} />
                    Unassigned Scheduled Rides ({unassignedRides.total})
                    {unassignedRides.urgent_count > 0 && (
                      <Badge className="bg-red-500 text-white animate-pulse">{unassignedRides.urgent_count} URGENT</Badge>
                    )}
                  </h3>
                  <Button variant="outline" size="sm" onClick={fetchUnassignedRides} disabled={isLoadingUnassigned}>
                    {isLoadingUnassigned ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                  </Button>
                </div>
                
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {unassignedRides.rides.map((ride) => (
                    <div key={ride.id} className={`p-3 bg-white rounded-lg border ${ride.is_urgent ? 'border-red-300' : 'border-amber-200'}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-2">
                            {ride.is_urgent && <Badge className="bg-red-500 text-white text-xs">URGENT</Badge>}
                            <span className={`text-sm font-bold ${ride.minutes_until_pickup <= 0 ? 'text-red-600' : ride.minutes_until_pickup <= 30 ? 'text-amber-600' : 'text-blue-600'}`}>
                              {ride.minutes_until_pickup <= 0 ? 'PAST DUE' : `${ride.minutes_until_pickup} min until pickup`}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mb-1">
                            <Clock size={12} className="inline mr-1" />
                            Scheduled: {ride.scheduled_time_formatted}
                          </p>
                          <p className="text-sm flex items-start gap-1">
                            <MapPin size={14} className="text-green-500 mt-0.5" />
                            <span className="truncate">{ride.pickup?.address || 'Unknown'}</span>
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            <User size={12} className="inline mr-1" />
                            {ride.passenger_name} • {ride.passenger_phone}
                          </p>
                          <p className="text-xs text-purple-600 mt-1">
                            <Car size={12} className="inline mr-1" />
                            {ride.vehicle_type}
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button 
                            size="sm" 
                            className="bg-blue-500 hover:bg-blue-600"
                            onClick={() => fetchNearbyDriversForRide(ride.id)}
                            disabled={selectedRideForAssignment === ride.id}
                          >
                            {selectedRideForAssignment === ride.id ? (
                              <Loader2 className="animate-spin mr-1" size={14} />
                            ) : (
                              <Users size={14} className="mr-1" />
                            )}
                            Find Drivers (10 min)
                          </Button>
                        </div>
                      </div>
                      
                      {/* Nearby Drivers List */}
                      {selectedRideForAssignment === ride.id && nearbyDriversForRide && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-sm">
                              Available Drivers ({nearbyDriversForRide.total})
                            </h4>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => { setNearbyDriversForRide(null); setSelectedRideForAssignment(null); }}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                          
                          {nearbyDriversForRide.drivers.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-4">
                              No drivers available within 10 minutes. Try expanding the search radius.
                            </p>
                          ) : (
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                              {nearbyDriversForRide.drivers.map((driver) => (
                                <div key={driver.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${driver.vehicle_match ? 'bg-green-100' : 'bg-gray-100'}`}>
                                      <Car size={18} className={driver.vehicle_match ? 'text-green-600' : 'text-gray-500'} />
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm">{driver.name}</p>
                                      <p className="text-xs text-gray-500">{driver.vehicle_type}</p>
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-green-600 font-medium">{driver.eta_minutes} min</span>
                                        <span className="text-gray-400">•</span>
                                        <span>{driver.distance_miles} mi</span>
                                        {driver.vehicle_match && (
                                          <>
                                            <span className="text-gray-400">•</span>
                                            <span className="text-green-600">✓ Match</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    className="bg-green-500 hover:bg-green-600"
                                    onClick={() => assignDriverToRide(ride.id, driver.id)}
                                    disabled={isAssigningDriver}
                                  >
                                    {isAssigningDriver ? <Loader2 className="animate-spin" size={14} /> : 'Assign'}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Main Rides List */}
            <Card className="p-6 border-2">
            <div className="flex gap-2 mb-6 flex-wrap">
              {['all', 'searching_driver', 'driver_assigned', 'in_progress', 'completed', 'cancelled'].map(status => (
                <Button key={status} variant={rideFilter === status ? 'default' : 'outline'} onClick={() => setRideFilter(status)}
                  className={rideFilter === status ? 'bg-amber-500 hover:bg-amber-600' : 'border-2'} size="sm">
                  {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Button>
              ))}
            </div>

            <div className="space-y-4">
              {rides.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Car size={48} className="mx-auto mb-4 opacity-50" /><p>No rides found</p>
                </div>
              ) : (
                rides.map((ride) => (
                  <Card key={ride.id} className="p-4 border hover:shadow-md transition-shadow" data-testid={`ride-card-${ride.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getStatusBadge(ride.status)}>{ride.status?.replace('_', ' ')}</Badge>
                          <span className="text-xs text-gray-500">{ride.id?.slice(0, 8)}...</span>
                        </div>
                        {/* Date/Time Info */}
                        <div className="flex items-center gap-4 mb-3 text-sm">
                          <div className="flex items-center gap-1 text-slate-600">
                            <Calendar size={14} className="text-amber-500" />
                            <span className="font-medium">
                              {ride.created_at ? new Date(ride.created_at).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', year: 'numeric' 
                              }) : 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-600">
                            <Clock size={14} className="text-amber-500" />
                            <span className="font-medium">
                              {ride.created_at ? new Date(ride.created_at).toLocaleTimeString('en-US', { 
                                hour: 'numeric', minute: '2-digit', hour12: true 
                              }) : 'N/A'}
                            </span>
                          </div>
                          {ride.scheduled_time && (
                            <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              <Calendar size={12} />
                              <span className="text-xs font-medium">
                                Scheduled: {new Date(ride.scheduled_time).toLocaleString('en-US', {
                                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-1 text-sm">
                          <p className="flex items-start gap-2">
                            <MapPin size={14} className="text-green-500 mt-1 flex-shrink-0" />
                            <span className="text-gray-700">{ride.pickup?.address || 'Unknown'}</span>
                          </p>
                          <p className="flex items-start gap-2">
                            <MapPin size={14} className="text-red-500 mt-1 flex-shrink-0" />
                            <span className="text-gray-700">{ride.dropoff?.address || 'Unknown'}</span>
                          </p>
                          {/* Passenger Info */}
                          {ride.passenger_name && (
                            <p className="flex items-center gap-2 text-gray-600 mt-2">
                              <User size={14} className="text-blue-500 flex-shrink-0" />
                              <span className="font-medium">{ride.passenger_name}</span>
                              {ride.passenger_phone && <span className="text-gray-400">• {ride.passenger_phone}</span>}
                            </p>
                          )}
                          {/* Driver Info - Prominent Display */}
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            {ride.driver_name ? (
                              <div className="bg-blue-50 rounded-lg p-2">
                                <p className="flex items-center gap-2 text-blue-800 font-medium">
                                  <Car size={14} className="text-blue-600" />
                                  Driver: {ride.driver_name}
                                </p>
                                {ride.driver_phone && (
                                  <p className="text-xs text-blue-600 ml-6">{ride.driver_phone}</p>
                                )}
                              </div>
                            ) : (
                              <p className="flex items-center gap-2 text-amber-600 text-xs">
                                <AlertCircle size={14} />
                                No driver assigned yet
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <div>
                          <p className="text-xl font-bold text-green-600">${ride.estimated_price || 0}</p>
                          <p className="text-sm text-gray-500">{ride.vehicle_type || 'sedan'}</p>
                        </div>
                        {/* Action Buttons for Active Rides */}
                        {!['completed', 'cancelled'].includes(ride.status) && (
                          <div className="flex gap-2 mt-2 flex-wrap justify-end">
                            <Button 
                              size="sm" 
                              onClick={() => handleCompleteRide(ride.id)}
                              disabled={completingRideId === ride.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`complete-ride-btn-${ride.id}`}
                            >
                              {completingRideId === ride.id ? (
                                <><Loader2 size={14} className="animate-spin mr-1" />...</>
                              ) : (
                                <><CheckCircle size={14} className="mr-1" />Complete</>
                              )}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleReassignRide(ride.id)}
                              disabled={reassigningRideId === ride.id}
                              className="border-amber-500 text-amber-600 hover:bg-amber-50"
                              data-testid={`reassign-ride-btn-${ride.id}`}
                            >
                              {reassigningRideId === ride.id ? (
                                <><Loader2 size={14} className="animate-spin mr-1" />...</>
                              ) : (
                                <><RefreshCw size={14} className="mr-1" />Reassign</>
                              )}
                            </Button>
                          </div>
                        )}
                        {ride.status === 'completed' && ride.driver_earnings > 0 && (
                          <p className="text-xs text-green-600">Driver earned: ${ride.driver_earnings}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </Card>
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div className="space-y-6">
            {/* PayPal Configuration Status */}
            {!payoutSettings.paypal_configured && (
              <Card className="p-6 border-2 border-amber-300 bg-amber-50">
                <div className="flex items-start gap-4">
                  <AlertCircle className="text-amber-600 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-amber-800">PayPal Not Configured</h3>
                    <p className="text-amber-700 text-sm mt-1">
                      To enable automatic PayPal payouts, add your PayPal API credentials to the backend environment:
                    </p>
                    <code className="block bg-amber-100 p-2 rounded mt-2 text-xs">
                      PAYPAL_CLIENT_ID=your_client_id<br/>
                      PAYPAL_CLIENT_SECRET=your_client_secret<br/>
                      PAYPAL_MODE=sandbox  {/* or 'live' for production */}
                    </code>
                    <p className="text-amber-700 text-sm mt-2">
                      Get credentials from: <a href="https://developer.paypal.com" target="_blank" rel="noopener noreferrer" className="underline">developer.paypal.com</a>
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Payout Summary */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="p-6 border-2 border-green-200 bg-green-50">
                <div className="flex items-center gap-3 mb-2">
                  <Banknote className="text-green-600" size={24} />
                  <span className="text-gray-600 font-medium">Pending Payouts</span>
                </div>
                <p className="text-3xl font-bold text-green-700">${pendingPayouts.total_pending?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-green-600">{pendingPayouts.driver_count || 0} drivers ready</p>
              </Card>
              
              <Card className="p-6 border-2">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="text-blue-600" size={24} />
                  <span className="text-gray-600 font-medium">Auto Payout</span>
                </div>
                <p className="text-xl font-bold">
                  {payoutSettings.auto_payout_enabled ? (
                    <span className="text-green-600">Enabled</span>
                  ) : (
                    <span className="text-gray-400">Disabled</span>
                  )}
                </p>
                <p className="text-sm text-gray-500">
                  {payoutSettings.auto_payout_enabled ? 
                    `Every ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][payoutSettings.auto_payout_day]}` : 
                    'Manual only'}
                </p>
              </Card>
              
              <Card className="p-6 border-2">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="text-amber-600" size={24} />
                  <span className="text-gray-600 font-medium">Min. Payout</span>
                </div>
                <p className="text-3xl font-bold">${payoutSettings.minimum_payout_amount || 10}</p>
                <p className="text-sm text-gray-500">Minimum threshold</p>
              </Card>
            </div>

            {/* Process Payouts Section */}
            <Card className="p-6 border-2">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Send className="text-amber-500" size={20} />
                  Process Payouts
                </h3>
                <div className="flex gap-2">
                  {pendingPayouts.drivers.length > 0 && (
                    <>
                      <Button variant="outline" onClick={selectAllDrivers} size="sm">
                        {selectedDriversForPayout.length === pendingPayouts.drivers.length ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button 
                        onClick={handleProcessAllPayouts} 
                        disabled={isProcessingPayout || !payoutSettings.paypal_configured}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="process-payouts-btn"
                      >
                        {isProcessingPayout ? (
                          <><Loader2 className="animate-spin mr-2" size={16} />Processing...</>
                        ) : (
                          <>
                            <Send size={16} className="mr-2" />
                            Pay {selectedDriversForPayout.length > 0 ? selectedDriversForPayout.length : 'All'} Drivers
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {pendingPayouts.drivers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle size={48} className="mx-auto mb-4 opacity-50 text-green-500" />
                  <p className="font-medium">All Caught Up!</p>
                  <p className="text-sm">No pending payouts at this time</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingPayouts.drivers.map((driver) => (
                    <div key={driver.driver_id} className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                      selectedDriversForPayout.includes(driver.driver_id) ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <div className="flex items-center gap-4">
                        <input 
                          type="checkbox" 
                          checked={selectedDriversForPayout.includes(driver.driver_id)}
                          onChange={() => toggleDriverSelection(driver.driver_id)}
                          className="w-5 h-5 rounded border-gray-300"
                        />
                        <div>
                          <p className="font-medium">{driver.name}</p>
                          <p className="text-sm text-gray-500">
                            PayPal: {driver.has_paypal ? driver.paypal_email : <span className="text-red-500">Not set</span>}
                          </p>
                          <p className="text-xs text-gray-400">
                            {driver.total_rides} rides • {driver.payout_percentage}% payout rate
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">${driver.driver_earnings?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">of ${driver.pending_amount?.toFixed(2)} gross</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleProcessSinglePayout(driver.driver_id, driver.name, driver.driver_earnings)}
                          disabled={isProcessingPayout || !payoutSettings.paypal_configured || !driver.has_paypal}
                          className="border-green-300 text-green-600 hover:bg-green-50"
                        >
                          <Send size={14} className="mr-1" />Pay
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Payout Settings */}
            <Card className="p-6 border-2">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-6">
                <Settings className="text-gray-500" size={20} />
                Payout Settings
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium">Automatic Weekly Payouts</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <button 
                      onClick={() => handleUpdatePayoutSettings({...payoutSettings, auto_payout_enabled: !payoutSettings.auto_payout_enabled})}
                      className={`relative w-12 h-6 rounded-full transition-colors ${payoutSettings.auto_payout_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${payoutSettings.auto_payout_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                    <span className="text-sm text-gray-600">
                      {payoutSettings.auto_payout_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Payout Day</Label>
                  <select 
                    value={payoutSettings.auto_payout_day}
                    onChange={(e) => handleUpdatePayoutSettings({...payoutSettings, auto_payout_day: parseInt(e.target.value)})}
                    className="w-full mt-2 p-2 border rounded-lg"
                    disabled={!payoutSettings.auto_payout_enabled}
                  >
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => (
                      <option key={day} value={i}>{day}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Minimum Payout Amount ($)</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    step="1"
                    value={payoutSettings.minimum_payout_amount}
                    onChange={(e) => handleUpdatePayoutSettings({...payoutSettings, minimum_payout_amount: parseFloat(e.target.value) || 10})}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Drivers must earn at least this amount to be included in payouts</p>
                </div>
              </div>
            </Card>

            {/* Payout History */}
            <Card className="p-6 border-2">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-6">
                <Clock className="text-gray-500" size={20} />
                Payout History
              </h3>
              
              {payoutHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No payout history yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payoutHistory.map((batch) => (
                    <div key={batch.batch_id} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Batch: {batch.batch_id}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(batch.created_at).toLocaleString()} • {batch.driver_count} drivers
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">${batch.total_amount?.toFixed(2)}</p>
                          <Badge className={batch.status === 'success' || batch.status === 'processing' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {batch.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* 1099 Tax Tab */}
        {activeTab === 'tax1099' && (
          <div className="space-y-6" data-testid="tax1099-tab">
            <Card className="p-6 border-2">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <FileText className="text-amber-500" size={20} />
                  1099-NEC Tax Forms
                </h3>
                <div className="flex items-center gap-3">
                  <Label className="whitespace-nowrap">Tax Year:</Label>
                  <select
                    value={tax1099Year}
                    onChange={(e) => setTax1099Year(parseInt(e.target.value))}
                    className="border-2 rounded-lg px-3 py-2"
                  >
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <Button
                    onClick={fetch1099Data}
                    disabled={isLoadingTax}
                    variant="outline"
                    className="border-2"
                  >
                    {isLoadingTax ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
                  </Button>
                </div>
              </div>

              {/* Summary Stats */}
              {tax1099Data && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-700">{tax1099Data.total_drivers}</p>
                    <p className="text-sm text-blue-600">Drivers (≥$600)</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">${tax1099Data.total_payouts?.toLocaleString()}</p>
                    <p className="text-sm text-green-600">Total Payouts</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{tax1099Data.drivers?.filter(d => d.form_generated).length || 0}</p>
                    <p className="text-sm text-amber-600">Forms Generated</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-700">{tax1099Data.drivers?.filter(d => d.form_sent).length || 0}</p>
                    <p className="text-sm text-purple-600">Forms Sent</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Button
                  onClick={handleGenerate1099}
                  disabled={isGenerating1099 || isLoadingTax}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {isGenerating1099 ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : (
                    <FileText size={18} className="mr-2" />
                  )}
                  Generate 1099 Forms
                </Button>
                <Button
                  onClick={handleSend1099ToAll}
                  disabled={isSending1099 || isLoadingTax || !tax1099Data?.total_drivers}
                  variant="outline"
                  className="border-2 border-green-500 text-green-600 hover:bg-green-50"
                >
                  {isSending1099 ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : (
                    <Send size={18} className="mr-2" />
                  )}
                  Mark All as Sent
                </Button>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-gray-50 rounded-lg mb-6">
                <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <AlertCircle size={16} />
                  IRS 1099-NEC Requirements
                </h4>
                <ul className="text-sm text-gray-600 space-y-1 list-disc pl-5">
                  <li>1099-NEC forms are required for contractors earning $600 or more in a calendar year</li>
                  <li>Forms must be sent to contractors by January 31st</li>
                  <li>Forms must be filed with the IRS by January 31st (or February 28th if filing by mail)</li>
                  <li>Box 1 shows total non-employee compensation (driver earnings + tips)</li>
                </ul>
              </div>

              {/* Driver List */}
              {isLoadingTax ? (
                <div className="text-center py-12">
                  <Loader2 className="animate-spin mx-auto mb-4" size={32} />
                  <p>Loading 1099 data...</p>
                </div>
              ) : !tax1099Data?.drivers?.length ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No drivers with ≥$600 earnings found for {tax1099Year}</p>
                  <p className="text-sm">Drivers must earn at least $600 to require a 1099</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700">Drivers Requiring 1099 ({tax1099Data.drivers.length})</h4>
                  {tax1099Data.drivers.map(driver => (
                    <Card key={driver.driver_id} className="p-4 border hover:border-amber-300 transition-colors">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h5 className="font-bold">{driver.driver_name}</h5>
                            {driver.form_sent && (
                              <Badge className="bg-green-100 text-green-700">Sent</Badge>
                            )}
                            {driver.form_generated && !driver.form_sent && (
                              <Badge className="bg-amber-100 text-amber-700">Generated</Badge>
                            )}
                            {driver.email_notification_sent && (
                              <Badge className="bg-blue-100 text-blue-700">📧 Emailed</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{driver.email}</p>
                          <p className="text-sm text-gray-500">EIN/SSN: {driver.ein_ssn || 'Not provided'}</p>
                        </div>
                        <div className="text-right">
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                            <span className="text-gray-500">Earnings:</span>
                            <span className="font-medium">${driver.total_earnings?.toLocaleString()}</span>
                            <span className="text-gray-500">Tips:</span>
                            <span className="font-medium">${driver.total_tips?.toLocaleString()}</span>
                            <span className="text-gray-500 font-semibold">Gross Income:</span>
                            <span className="font-bold text-green-600">${driver.gross_income?.toLocaleString()}</span>
                            <span className="text-gray-500">Total Rides:</span>
                            <span className="font-medium">{driver.total_rides}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            {/* Referral Settings Card */}
            <Card className="p-6 border-2" data-testid="referral-settings-card">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-6">
                <Gift className="text-amber-500" size={20} />
                Referral Program Settings
              </h3>
              
              {isLoadingReferrals ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-amber-500" size={32} />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Enable/Disable Program */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Referral Program</p>
                      <p className="text-sm text-gray-500">Enable or disable the referral program</p>
                    </div>
                    <Button
                      variant={referralSettings.enabled ? "default" : "outline"}
                      onClick={() => setReferralSettings({...referralSettings, enabled: !referralSettings.enabled})}
                      className={referralSettings.enabled ? "bg-green-500 hover:bg-green-600" : ""}
                    >
                      {referralSettings.enabled ? <ToggleRight size={20} className="mr-2" /> : <ToggleLeft size={20} className="mr-2" />}
                      {referralSettings.enabled ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>

                  {/* Reward Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Referrer Reward ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={referralSettings.referrer_reward}
                        onChange={(e) => setReferralSettings({...referralSettings, referrer_reward: parseFloat(e.target.value) || 0})}
                        className="mt-1 border-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">Amount given to the person who referred</p>
                    </div>
                    <div>
                      <Label>Referee Reward ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={referralSettings.referee_reward}
                        onChange={(e) => setReferralSettings({...referralSettings, referee_reward: parseFloat(e.target.value) || 0})}
                        className="mt-1 border-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">Amount given to the new user who signed up</p>
                    </div>
                  </div>

                  <div>
                    <Label>Number of Discounted Rides</Label>
                    <Input
                      type="number"
                      min="1"
                      value={referralSettings.min_rides_for_reward}
                      onChange={(e) => setReferralSettings({...referralSettings, min_rides_for_reward: parseInt(e.target.value) || 1})}
                      className="mt-1 border-2 max-w-xs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Discount is spread across this many rides. 
                      (e.g., ${referralSettings.referee_reward || 5} ÷ {referralSettings.min_rides_for_reward || 5} = ${((referralSettings.referee_reward || 5) / (referralSettings.min_rides_for_reward || 5)).toFixed(2)} per ride)
                    </p>
                  </div>

                  <Button
                    onClick={saveReferralSettings}
                    disabled={isSavingReferralSettings}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    {isSavingReferralSettings ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle size={16} className="mr-2" />}
                    Save Settings
                  </Button>
                </div>
              )}
            </Card>

            {/* Referral Stats Card */}
            {referralStats && (
              <Card className="p-6 border-2">
                <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                  <BarChart3 className="text-blue-500" size={20} />
                  Referral Statistics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{referralStats.total_referrals || 0}</p>
                    <p className="text-sm text-gray-600">Total Referrals</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{referralStats.pending_referrals || 0}</p>
                    <p className="text-sm text-gray-600">Pending</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{referralStats.completed_referrals || 0}</p>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">${(referralStats.total_rewards_given || 0).toFixed(2)}</p>
                    <p className="text-sm text-gray-600">Total Rewards Given</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Driver Referral Program Settings */}
            <Card className="p-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-6 text-purple-800">
                <Car className="text-purple-500" size={20} />
                Driver Referral Program Settings
              </h3>
              
              <div className="space-y-4">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-purple-200">
                  <div>
                    <p className="font-medium text-purple-800">Driver Referral Program</p>
                    <p className="text-sm text-gray-500">Drivers can refer other drivers for rewards</p>
                  </div>
                  <Button
                    variant={driverReferralSettings.enabled ? "default" : "outline"}
                    onClick={() => setDriverReferralSettings({...driverReferralSettings, enabled: !driverReferralSettings.enabled})}
                    className={driverReferralSettings.enabled ? "bg-purple-500 hover:bg-purple-600" : ""}
                  >
                    {driverReferralSettings.enabled ? <ToggleRight size={20} className="mr-2" /> : <ToggleLeft size={20} className="mr-2" />}
                    {driverReferralSettings.enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                
                {/* Reward Amounts */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-purple-800">Referrer Reward ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={driverReferralSettings.referrer_reward}
                      onChange={(e) => setDriverReferralSettings({...driverReferralSettings, referrer_reward: parseFloat(e.target.value) || 0})}
                      className="mt-1 border-2 border-purple-300 focus:border-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Amount for existing driver</p>
                  </div>
                  <div>
                    <Label className="text-purple-800">New Driver Reward ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={driverReferralSettings.referee_reward}
                      onChange={(e) => setDriverReferralSettings({...driverReferralSettings, referee_reward: parseFloat(e.target.value) || 0})}
                      className="mt-1 border-2 border-purple-300 focus:border-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Amount for new driver</p>
                  </div>
                  <div>
                    <Label className="text-purple-800">Required Rides</Label>
                    <Input
                      type="number"
                      min="1"
                      value={driverReferralSettings.min_rides_for_reward}
                      onChange={(e) => setDriverReferralSettings({...driverReferralSettings, min_rides_for_reward: parseInt(e.target.value) || 1})}
                      className="mt-1 border-2 border-purple-300 focus:border-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Rides new driver must complete</p>
                  </div>
                </div>
                
                <Button
                  onClick={saveDriverReferralSettings}
                  disabled={isSavingDriverReferralSettings}
                  className="bg-purple-500 hover:bg-purple-600"
                >
                  {isSavingDriverReferralSettings ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle size={16} className="mr-2" />}
                  Save Driver Referral Settings
                </Button>
              </div>
              
              {/* Driver Referral Stats */}
              {driverReferralStats && (
                <div className="mt-6 pt-6 border-t border-purple-200">
                  <h4 className="font-medium text-purple-800 mb-3">Driver Referral Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <p className="text-2xl font-bold text-purple-600">{driverReferralStats.total_referrals || 0}</p>
                      <p className="text-xs text-gray-600">Total Referrals</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <p className="text-2xl font-bold text-amber-600">{driverReferralStats.pending_referrals || 0}</p>
                      <p className="text-xs text-gray-600">Pending</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <p className="text-2xl font-bold text-green-600">{driverReferralStats.completed_referrals || 0}</p>
                      <p className="text-xs text-gray-600">Completed</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <p className="text-2xl font-bold text-blue-600">${driverReferralStats.total_paid || 0}</p>
                      <p className="text-xs text-gray-600">Total Paid</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Driver Referral Payouts Management */}
            <Card className="p-6 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="driver-referral-payouts-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Banknote className="text-green-600" size={20} />
                  Driver Referral Payouts
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchDriverReferralPayouts}
                  disabled={isLoadingDriverPayouts}
                >
                  {isLoadingDriverPayouts ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                </Button>
              </div>
              
              {/* Payout Summary */}
              {driverReferralPayouts.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-white rounded-lg p-3 text-center border border-green-200">
                    <p className="text-2xl font-bold text-green-600">${driverReferralPayouts.summary.total_pending_amount || 0}</p>
                    <p className="text-xs text-gray-600">Total Pending</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-green-200">
                    <p className="text-2xl font-bold text-amber-600">{driverReferralPayouts.summary.pending_referrer_payouts || 0}</p>
                    <p className="text-xs text-gray-600">Referrers Unpaid</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-green-200">
                    <p className="text-2xl font-bold text-purple-600">{driverReferralPayouts.summary.pending_referee_payouts || 0}</p>
                    <p className="text-xs text-gray-600">Referees Unpaid</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-green-200">
                    <p className="text-2xl font-bold text-slate-600">{driverReferralPayouts.summary.min_rides_required || 10}</p>
                    <p className="text-xs text-gray-600">Min Rides Required</p>
                  </div>
                </div>
              )}
              
              {/* Payouts List */}
              {isLoadingDriverPayouts ? (
                <div className="text-center py-8">
                  <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                  <p className="text-gray-500">Loading payouts...</p>
                </div>
              ) : driverReferralPayouts.referrals?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No driver referrals yet</p>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {driverReferralPayouts.referrals?.map((ref) => (
                    <div key={ref.id} className="p-4 bg-white rounded-xl border border-green-200 shadow-sm">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        {/* Referrer Info */}
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-xs text-gray-400 uppercase mb-1">Referrer (Gets ${ref.referrer_reward})</p>
                          <p className="font-semibold text-green-700">{ref.referrer_name}</p>
                          <p className="text-sm text-gray-500">{ref.referrer_email}</p>
                          <div className="mt-2">
                            {ref.referrer_paid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <CheckCircle size={12} /> Paid {ref.referrer_paid_at ? new Date(ref.referrer_paid_at).toLocaleDateString() : ''}
                              </span>
                            ) : ref.eligible_for_payout ? (
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 h-7 text-xs"
                                onClick={() => markReferralPaid(ref.id, 'referrer')}
                                disabled={isMarkingPaid === ref.id}
                                data-testid={`mark-referrer-paid-${ref.id}`}
                              >
                                {isMarkingPaid === ref.id ? <Loader2 className="animate-spin mr-1" size={12} /> : <DollarSign size={12} className="mr-1" />}
                                Mark Paid ${ref.referrer_reward}
                              </Button>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                <Clock size={12} /> Waiting ({ref.referee_rides}/{ref.min_rides_required} rides)
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Arrow */}
                        <div className="hidden md:flex items-center justify-center px-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="text-green-600" size={16} />
                          </div>
                        </div>
                        
                        {/* Referee Info */}
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-xs text-gray-400 uppercase mb-1">New Driver (Gets ${ref.referee_reward})</p>
                          <p className="font-semibold text-purple-700">{ref.referee_name}</p>
                          <p className="text-sm text-gray-500">{ref.referee_email}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Rides: <span className={ref.eligible_for_payout ? 'text-green-600 font-semibold' : 'text-amber-600'}>{ref.referee_rides}/{ref.min_rides_required}</span>
                          </p>
                          <div className="mt-2">
                            {ref.referee_paid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <CheckCircle size={12} /> Paid {ref.referee_paid_at ? new Date(ref.referee_paid_at).toLocaleDateString() : ''}
                              </span>
                            ) : ref.eligible_for_payout ? (
                              <Button
                                size="sm"
                                className="bg-purple-500 hover:bg-purple-600 h-7 text-xs"
                                onClick={() => markReferralPaid(ref.id, 'referee')}
                                disabled={isMarkingPaid === ref.id}
                                data-testid={`mark-referee-paid-${ref.id}`}
                              >
                                {isMarkingPaid === ref.id ? <Loader2 className="animate-spin mr-1" size={12} /> : <DollarSign size={12} className="mr-1" />}
                                Mark Paid ${ref.referee_reward}
                              </Button>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                                <Clock size={12} /> Not eligible yet
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Overall Status & Pay Both */}
                        <div className="w-full md:w-auto flex flex-col items-center gap-2 pt-2 md:pt-0 border-t md:border-t-0 md:border-l md:pl-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            ref.status === 'completed' ? 'bg-green-100 text-green-700' :
                            ref.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {ref.status}
                          </span>
                          <p className="text-xs text-gray-400">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </p>
                          {ref.eligible_for_payout && !ref.referrer_paid && !ref.referee_paid && (
                            <Button
                              size="sm"
                              className="bg-blue-500 hover:bg-blue-600 h-8 text-xs mt-2"
                              onClick={() => markReferralPaid(ref.id, 'both')}
                              disabled={isMarkingPaid === ref.id}
                              data-testid={`mark-both-paid-${ref.id}`}
                            >
                              {isMarkingPaid === ref.id ? <Loader2 className="animate-spin mr-1" size={12} /> : <CheckCircle size={12} className="mr-1" />}
                              Pay Both (${ref.referrer_reward + ref.referee_reward})
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Recent Referrals List */}
            <Card className="p-6 border-2">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                <Users className="text-slate-600" size={20} />
                Recent Referrals
              </h3>
              {referralList.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No referrals yet</p>
              ) : (
                <div className="space-y-3">
                  {referralList.map((ref) => (
                    <div key={ref.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{ref.referee_name}</p>
                        <p className="text-sm text-gray-500">{ref.referee_email}</p>
                      </div>
                      <div className="text-center px-4">
                        <p className="text-xs text-gray-400">Referred by</p>
                        <p className="font-medium text-amber-600">{ref.referrer_name}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          ref.status === 'completed' ? 'bg-green-100 text-green-700' :
                          ref.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {ref.status}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className="space-y-6">
            <Card className="p-6 border-2" data-testid="tips-settings-card">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-6">
                <Gift className="text-green-500" size={20} />
                Tip Settings
              </h3>
              
              <div className="space-y-6">
                {/* Enable/Disable Tips */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Enable Tips</p>
                    <p className="text-sm text-gray-500">Allow users to tip drivers after rides</p>
                  </div>
                  <Button
                    variant={tipSettings.enabled ? "default" : "outline"}
                    onClick={() => setTipSettings({...tipSettings, enabled: !tipSettings.enabled})}
                    className={tipSettings.enabled ? "bg-green-500 hover:bg-green-600" : ""}
                  >
                    {tipSettings.enabled ? <ToggleRight size={20} className="mr-2" /> : <ToggleLeft size={20} className="mr-2" />}
                    {tipSettings.enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>

                {/* Preset Amounts */}
                <div>
                  <Label className="text-sm font-medium">Preset Tip Amounts ($)</Label>
                  <p className="text-xs text-gray-500 mb-2">These amounts will be shown as quick-select options</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tipSettings.preset_amounts?.map((amount, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="1"
                          value={amount}
                          onChange={(e) => {
                            const newAmounts = [...tipSettings.preset_amounts];
                            newAmounts[index] = parseFloat(e.target.value) || 0;
                            setTipSettings({...tipSettings, preset_amounts: newAmounts});
                          }}
                          className="w-20"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newAmounts = tipSettings.preset_amounts.filter((_, i) => i !== index);
                            setTipSettings({...tipSettings, preset_amounts: newAmounts});
                          }}
                        >
                          <XCircle size={16} className="text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTipSettings({...tipSettings, preset_amounts: [...(tipSettings.preset_amounts || []), 20]})}
                    >
                      <Plus size={16} className="mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Custom Amount */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Allow Custom Amount</p>
                    <p className="text-sm text-gray-500">Let users enter a custom tip amount</p>
                  </div>
                  <Button
                    variant={tipSettings.custom_allowed ? "default" : "outline"}
                    onClick={() => setTipSettings({...tipSettings, custom_allowed: !tipSettings.custom_allowed})}
                    className={tipSettings.custom_allowed ? "bg-green-500 hover:bg-green-600" : ""}
                  >
                    {tipSettings.custom_allowed ? 'Allowed' : 'Not Allowed'}
                  </Button>
                </div>

                {/* Default Percentage */}
                <div>
                  <Label className="text-sm font-medium">Default Tip Percentage (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={tipSettings.default_percentage}
                    onChange={(e) => setTipSettings({...tipSettings, default_percentage: parseFloat(e.target.value) || 15})}
                    className="w-32 mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Suggested tip shown to users (as % of ride fare)</p>
                </div>

                <Button onClick={updateTipSettings} className="bg-amber-500 hover:bg-amber-600">
                  Save Tip Settings
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promos' && (
          <div className="space-y-6">
            {/* Create New Promo */}
            <Card className="p-6 border-2" data-testid="create-promo-card">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-6">
                <Plus className="text-amber-500" size={20} />
                Create Promo Code
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Promo Code *</Label>
                  <Input
                    placeholder="e.g., SUMMER25"
                    value={newPromo.code}
                    onChange={(e) => setNewPromo({...newPromo, code: e.target.value.toUpperCase()})}
                    className="mt-1"
                    data-testid="input-promo-code"
                  />
                </div>
                
                <div>
                  <Label>Discount Type</Label>
                  <select
                    value={newPromo.discount_type}
                    onChange={(e) => setNewPromo({...newPromo, discount_type: e.target.value})}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    <option value="percentage">Percentage Off (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                
                <div>
                  <Label>Discount Value</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newPromo.discount_value}
                    onChange={(e) => setNewPromo({...newPromo, discount_value: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {newPromo.discount_type === 'percentage' ? 'Percentage off total fare' : 'Dollar amount off'}
                  </p>
                </div>
                
                <div>
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newPromo.max_uses}
                    onChange={(e) => setNewPromo({...newPromo, max_uses: parseInt(e.target.value) || 100})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Valid Until (Optional)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="date"
                      value={newPromo.valid_until}
                      onChange={(e) => setNewPromo({...newPromo, valid_until: e.target.value})}
                      className="flex-1"
                      min={new Date().toISOString().split('T')[0]}
                      disabled={newPromo.valid_until === 'never'}
                    />
                    <Button
                      type="button"
                      variant={!newPromo.valid_until ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNewPromo({...newPromo, valid_until: ''})}
                      className={!newPromo.valid_until ? "bg-green-500 hover:bg-green-600 whitespace-nowrap" : "whitespace-nowrap"}
                    >
                      Never Expires
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {!newPromo.valid_until ? '✓ This promo code will never expire' : 'Set an expiration date or click "Never Expires"'}
                  </p>
                </div>
                
                <div>
                  <Label>Minimum Ride Amount ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newPromo.min_ride_amount}
                    onChange={(e) => setNewPromo({...newPromo, min_ride_amount: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label>Description (optional)</Label>
                  <Input
                    placeholder="e.g., Summer sale - 25% off all rides!"
                    value={newPromo.description}
                    onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <Button onClick={createPromoCode} className="mt-4 bg-amber-500 hover:bg-amber-600">
                <Plus size={16} className="mr-2" /> Create Promo Code
              </Button>
            </Card>

            {/* Existing Promo Codes */}
            <Card className="p-6 border-2" data-testid="promo-codes-list">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-6">
                <Tag className="text-blue-500" size={20} />
                Active Promo Codes ({promoCodes.length})
              </h3>
              
              {isLoadingPromos ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-amber-500" size={24} />
                </div>
              ) : promoCodes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Tag size={48} className="mx-auto mb-4 opacity-20" />
                  <p>No promo codes created yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {promoCodes.map((promo) => (
                    <div 
                      key={promo.id} 
                      className={`p-4 rounded-lg border-2 ${promo.is_active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`px-3 py-1 rounded-lg font-mono font-bold ${promo.is_active ? 'bg-green-200 text-green-800' : 'bg-gray-300 text-gray-600'}`}>
                            {promo.code}
                          </div>
                          <div>
                            <p className="font-medium">
                              {promo.discount_type === 'percentage' 
                                ? `${promo.discount_value}% off` 
                                : `$${promo.discount_value} off`}
                            </p>
                            {promo.description && (
                              <p className="text-sm text-gray-500">{promo.description}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant={promo.is_active ? "default" : "secondary"}>
                            {promo.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePromoStatus(promo.id, promo.is_active)}
                          >
                            {promo.is_active ? <ToggleRight className="text-green-500" /> : <ToggleLeft className="text-gray-400" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePromoCode(promo.id)}
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span>Uses: {promo.times_used || 0}/{promo.max_uses}</span>
                        {promo.min_ride_amount > 0 && <span>Min: ${promo.min_ride_amount}</span>}
                        <span className={!promo.valid_until ? 'text-green-600 font-medium' : ''}>
                          {promo.valid_until ? `Expires: ${new Date(promo.valid_until).toLocaleDateString()}` : '✓ Never Expires'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Terms & Conditions Tab */}
        {activeTab === 'terms' && (
          <div className="space-y-6">
            <Card className="p-6 border-2">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <FileText className="text-amber-500" />
                    Terms & Conditions
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    These terms apply to both Users (before each ride) and Drivers (during onboarding)
                  </p>
                </div>
                <Button 
                  onClick={saveTermsConditions}
                  disabled={isSavingTerms}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="save-terms-btn"
                >
                  {isSavingTerms ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="mr-2" />
                      Save Terms
                    </>
                  )}
                </Button>
              </div>

              {isLoadingTerms ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-amber-500" size={32} />
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="terms-title" className="text-sm font-medium">Title</Label>
                    <Input
                      id="terms-title"
                      value={termsTitle}
                      onChange={(e) => setTermsTitle(e.target.value)}
                      placeholder="Terms & Conditions"
                      className="mt-1"
                      data-testid="terms-title-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="terms-content" className="text-sm font-medium">Content</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Enter your terms and conditions below. This will be shown to users before booking and drivers during signup.
                    </p>
                    <Textarea
                      id="terms-content"
                      value={termsContent}
                      onChange={(e) => setTermsContent(e.target.value)}
                      placeholder="Enter your Terms & Conditions here...

Example:
1. Service Agreement
By using J-Style Services, you agree to...

2. User Responsibilities
Users must provide accurate information...

3. Payment Terms
All payments are processed securely through Stripe...

4. Cancellation Policy
Rides may be cancelled up to 5 minutes before pickup..."
                      className="mt-1 min-h-[400px] font-mono text-sm"
                      data-testid="terms-content-input"
                    />
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h3 className="font-semibold text-amber-800 mb-2">Preview</h3>
                    <div className="bg-white rounded-lg p-4 max-h-[300px] overflow-y-auto border">
                      <h4 className="font-bold text-lg mb-2">{termsTitle || 'Terms & Conditions'}</h4>
                      {termsContent ? (
                        <div className="whitespace-pre-wrap text-sm text-gray-700">
                          {termsContent}
                        </div>
                      ) : (
                        <p className="text-gray-400 italic">No content yet. Enter your terms above.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Website CMS Tab */}
        {activeTab === 'website' && (
          <div className="space-y-6">
            <Card className="p-6 border-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Globe className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Website Content</h2>
                    <p className="text-sm text-gray-500">Edit footer sections, About Us, Privacy Policy, and Terms</p>
                  </div>
                </div>
                <Button variant="outline" onClick={fetchCMSContent} disabled={cmsLoading}>
                  <RefreshCw size={16} className={`mr-2 ${cmsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {cmsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin" size={32} />
                </div>
              ) : cmsContent ? (
                <div className="space-y-6">
                  {/* Footer Sections */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Company Section */}
                    <Card className="p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-800">
                          {cmsContent.company_section?.title || 'Company'}
                        </h3>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingSection('company_section');
                            setSectionForm(cmsContent.company_section || { title: 'Company', links: [] });
                          }}
                        >
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                      </div>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {(cmsContent.company_section?.links || []).map((link, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                            {link.label} → {link.url}
                          </li>
                        ))}
                      </ul>
                    </Card>

                    {/* Services Section */}
                    <Card className="p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-800">
                          {cmsContent.services_section?.title || 'Services'}
                        </h3>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingSection('services_section');
                            setSectionForm(cmsContent.services_section || { title: 'Services', links: [] });
                          }}
                        >
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                      </div>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {(cmsContent.services_section?.links || []).map((link, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                            {link.label} → {link.url}
                          </li>
                        ))}
                      </ul>
                    </Card>

                    {/* Support Section */}
                    <Card className="p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-800">
                          {cmsContent.support_section?.title || 'Support'}
                        </h3>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingSection('support_section');
                            setSectionForm(cmsContent.support_section || { title: 'Support', links: [] });
                          }}
                        >
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                      </div>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {(cmsContent.support_section?.links || []).map((link, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                            {link.label} → {link.url}
                          </li>
                        ))}
                      </ul>
                    </Card>

                    {/* Resources Section */}
                    <Card className="p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-800">
                          {cmsContent.resources_section?.title || 'Resources'}
                        </h3>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingSection('resources_section');
                            setSectionForm(cmsContent.resources_section || { title: 'Resources', links: [] });
                          }}
                        >
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                      </div>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {(cmsContent.resources_section?.links || []).map((link, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                            {link.label} → {link.url}
                          </li>
                        ))}
                      </ul>
                    </Card>
                  </div>

                  {/* Copyright Text */}
                  <Card className="p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-800">Copyright Text</h3>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={cmsContent.copyright_text || ''}
                        onChange={(e) => setCmsContent(prev => ({ ...prev, copyright_text: e.target.value }))}
                        placeholder="© 2025 J-Style Services. All rights reserved."
                      />
                      <Button onClick={() => saveCMSPageContent('copyright_text', cmsContent.copyright_text)}>
                        <Save size={16} className="mr-1" /> Save
                      </Button>
                    </div>
                  </Card>

                  {/* Page Content Editors */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <FileText size={20} /> Page Content
                    </h3>
                    
                    {/* About Us Page */}
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-700">About Us Page</h4>
                      </div>
                      <Textarea
                        value={cmsContent.about_us_page || ''}
                        onChange={(e) => setCmsContent(prev => ({ ...prev, about_us_page: e.target.value }))}
                        placeholder="Enter About Us content (supports Markdown)"
                        rows={8}
                        className="font-mono text-sm"
                      />
                      <Button className="mt-2" onClick={() => saveCMSPageContent('about_us_page', cmsContent.about_us_page)}>
                        <Save size={16} className="mr-1" /> Save About Us
                      </Button>
                    </Card>

                    {/* Privacy Policy */}
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-700">Privacy Policy</h4>
                      </div>
                      <Textarea
                        value={cmsContent.privacy_policy || ''}
                        onChange={(e) => setCmsContent(prev => ({ ...prev, privacy_policy: e.target.value }))}
                        placeholder="Enter Privacy Policy content"
                        rows={8}
                        className="font-mono text-sm"
                      />
                      <Button className="mt-2" onClick={() => saveCMSPageContent('privacy_policy', cmsContent.privacy_policy)}>
                        <Save size={16} className="mr-1" /> Save Privacy Policy
                      </Button>
                    </Card>

                    {/* Terms of Service */}
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-700">Terms of Service</h4>
                      </div>
                      <Textarea
                        value={cmsContent.terms_of_service || ''}
                        onChange={(e) => setCmsContent(prev => ({ ...prev, terms_of_service: e.target.value }))}
                        placeholder="Enter Terms of Service content"
                        rows={8}
                        className="font-mono text-sm"
                      />
                      <Button className="mt-2" onClick={() => saveCMSPageContent('terms_of_service', cmsContent.terms_of_service)}>
                        <Save size={16} className="mr-1" /> Save Terms of Service
                      </Button>
                    </Card>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Globe size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Click "Refresh" to load website content</p>
                </div>
              )}
            </Card>

            {/* Section Editor Modal */}
            {editingSection && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Edit {editingSection.replace('_section', '').replace('_', ' ').toUpperCase()}</h3>
                    <Button variant="ghost" size="sm" onClick={() => setEditingSection(null)}>
                      <X size={20} />
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Section Title</Label>
                      <Input
                        value={sectionForm.title}
                        onChange={(e) => setSectionForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Section title"
                      />
                    </div>
                    
                    <div>
                      <Label>Links</Label>
                      <div className="space-y-2 mt-2">
                        {sectionForm.links?.map((link, idx) => (
                          <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded">
                            <Input
                              value={link.label}
                              onChange={(e) => updateLinkInSection(idx, 'label', e.target.value)}
                              placeholder="Label"
                              className="flex-1"
                            />
                            <Input
                              value={link.url}
                              onChange={(e) => updateLinkInSection(idx, 'url', e.target.value)}
                              placeholder="/page or https://..."
                              className="flex-1"
                            />
                            <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={link.is_internal}
                                onChange={(e) => updateLinkInSection(idx, 'is_internal', e.target.checked)}
                              />
                              Internal
                            </label>
                            <Button variant="ghost" size="sm" onClick={() => removeLinkFromSection(idx)}>
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" className="mt-2" onClick={addLinkToSection}>
                        <Plus size={16} className="mr-1" /> Add Link
                      </Button>
                    </div>
                    
                    <div className="flex gap-2 pt-4">
                      <Button variant="outline" onClick={() => setEditingSection(null)} className="flex-1">
                        Cancel
                      </Button>
                      <Button onClick={() => saveCMSSection(editingSection, sectionForm)} className="flex-1 bg-amber-500 hover:bg-amber-600">
                        <Save size={16} className="mr-1" /> Save Changes
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPortal;
